/**
 * Shopify-level store templates.
 * Each template is a complete layout_config for a specific retail vertical.
 * All blocks are from the registered block registry — no custom logic needed.
 */
import type { LayoutConfig } from '@/storefront-runtime/types'
import { validateAllTemplates, validateBlockCatalogContract } from '@/lib/blocks/validateTemplateBlocks'
import {
  futurerPremiumCategoryBlocks,
  futurerPremiumHomeBlocks,
  futurerPremiumProductBlocks,
  futurerPremiumShopBlocks,
} from '@/data/futurerPremiumTemplate'
import { proStoreTemplates } from './storeTemplatesPro'
import { fashionEditorialTemplate } from '@/data/fashionv2lTemplate'

export type StoreTemplate = {
  id: string
  name: string
  description: string
  niche: string
  thumbnail: string
  layout: LayoutConfig
}

// ─── BEAUTY / COSMETICS ───────────────────────────────────────────────────────

const beautyTemplate: StoreTemplate = {
  id: 'beauty',
  name: 'Beauté & Cosmétiques',
  description: 'Template haut de gamme pour marques de beauté, soins de la peau et maquillage.',
  niche: 'Beauté',
  thumbnail: '/templates/local-webp/bty-thumb.webp',
  layout: {
    theme: {
      id: 'aura',
      primary: 'hsl(350 65% 52%)',
      secondary: 'hsl(350 40% 96%)',
      backgroundColor: '#fffaf9',
      font: 'Outfit',
      radius: '0.75rem',
      ui: {
        productCard: 'luxury',
        buttons: 'pill',
        spacing: 'airy',
        headingStyle: 'editorial',
        font: 'serif',
        density: 'comfortable',
        mode: 'light',
        radius: 'xl',
      },
    } as any,
    header: {
      props: {
        storeName: null,
        menuItems: [
          { label: 'Accueil', link: '/' },
          { label: 'Boutique', link: '/shop' },
          { label: 'À Propos', link: '/about' },
          { label: 'FAQ', link: '/faq' },
          { label: 'Contact', link: '/contact' },
        ],
        showSearch: true,
        showCart: true,
        sticky: true,
        variant: 'default',
      },
    },
    footer: {
      props: {
        storeName: true,
        description: 'Des produits de beauté soigneusement sélectionnés pour sublimer votre routine quotidienne.',
        copyright: `© ${new Date().getFullYear()} Votre Boutique. Tous droits réservés.`,
        columns: [
          {
            title: 'Boutique',
            links: [
              { label: 'Tous les produits', link: '/shop' },
              { label: 'Soins du visage', link: '/category/soins-visage' },
              { label: 'Maquillage', link: '/category/maquillage' },
              { label: 'Parfums', link: '/category/parfums' },
            ],
          },
          {
            title: 'Service Client',
            links: [
              { label: 'FAQ', link: '/faq' },
              { label: 'Livraison', link: '/policy/shipping' },
              { label: 'Retours', link: '/policy/returns' },
              { label: 'Contact', link: '/contact' },
            ],
          },
          {
            title: 'La Marque',
            links: [
              { label: 'Notre Histoire', link: '/about' },
            ],
          },
        ],
        socialLinks: {
          instagram: 'https://instagram.com',
          facebook: 'https://facebook.com',
          tiktok: 'https://tiktok.com',
        },
        showPaymentIcons: true,
      },
    },
    pages: {
      home: [
        // ── 1. Announce bar (always visible, builds anticipation) ────────────
        {
          id: 'beauty-home-announce',
          type: 'announcement-bar',
          visible: true,
          props: {
            text: '✨ Livraison gratuite dès 100 TND • Produits certifiés dermatologiquement • Retours 7 jours',
            backgroundColor: '#1a0a0a',
          },
        },
        // ── 2. Hero — split layout (text + brand panel) ──────────────────────
        // More emotional impact than image-banner; the split shows product context
        {
          id: 'beauty-home-hero',
          type: 'hero',
          visible: true,
          props: {
            headline: 'Votre Routine, Réinventée',
            subtitle: 'Des formules développées par des dermatologues pour une peau visiblement transformée en 30 jours.',
            ctaLabel: 'Découvrir la collection',
            anchorId: 'collection',
            backgroundImage: '/templates/local-webp/bty-hero.webp',
            layout: 'split',
            overlayOpacity: 20,
            textColor: '',
          },
        },
        // ── 3. Science / ingredients — rational proof before generic trust badges
        {
          id: 'beauty-home-story1',
          type: 'image-with-text',
          visible: true,
          props: {
            image: '/templates/local-webp/bty-story-science.webp',
            imageAlt: 'Ingrédients actifs soigneusement sélectionnés',
            title: 'La Science au Service de la Beauté',
            text: "Chaque produit est formulé avec des ingrédients actifs cliniquement prouvés. Nous travaillons avec des laboratoires certifiés pour garantir efficacité, tolérance et plaisir d'utilisation — parce que prendre soin de soi devrait être un moment de plaisir, pas de compromis.",
            ctaLabel: 'Notre approche',
            ctaLink: '/about',
            layout: 'image-right',
            imageAspect: 'portrait',
          },
        },
        // ── 4. Trust strip (after the science narrative)
        {
          id: 'beauty-home-trust',
          type: 'trust-strip',
          visible: true,
          props: {},
        },
        // ── 5. Category strip (quick browse by skin type / category) ────────
        {
          id: 'beauty-home-cats',
          type: 'category-strip',
          visible: true,
          props: {},
        },
        // ── 6. Best-sellers (social proof via popular items) ─────────────────
        {
          id: 'beauty-home-featured',
          type: 'featured-collection',
          visible: true,
          props: {
            title: 'Les Best-Sellers',
            subtitle: 'Les produits plébiscités par notre communauté',
            limit: 8,
            columns: 4,
            viewAllLabel: 'Voir tout',
            viewAllLink: '/shop',
          },
        },
        // ── 7. Full catalog grid ─────────────────────────────────────────────
        {
          id: 'beauty-home-grid',
          type: 'product-grid',
          visible: true,
          props: {
            title: 'Toute la Collection',
            subtitle: 'Soins, maquillage et bien-être — tout ce dont vous avez besoin.',
            limit: 12,
            columns: 4,
            anchorId: 'collection',
          },
        },
        // ── 8. Story block 2 — brand values / sustainability ─────────────────
        // Second story deepens brand identity before the conversion strip
        {
          id: 'beauty-home-story2',
          type: 'image-with-text',
          visible: true,
          props: {
            image: '/templates/local-webp/bty-story-rituel.webp',
            imageAlt: 'Rituel beauté quotidien',
            title: 'Un Geste, Un Rituel',
            text: "Nous croyons que la beauté n'est pas une correction, mais une célébration. Chaque soin est formulé pour s'intégrer naturellement dans votre quotidien — efficace, agréable, et respectueux de votre peau.",
            ctaLabel: 'Découvrir nos valeurs',
            ctaLink: '/about',
            layout: 'image-left',
            imageAspect: 'landscape',
          },
        },
        // ── 9. Testimonials (close to purchase CTA = maximum conversion lift) ─
        {
          id: 'beauty-home-testimonials',
          type: 'testimonials',
          visible: true,
          props: {
            title: 'Ce Que Disent Nos Clientes',
            subtitle: 'Avis 100% vérifiés de vraies acheteuses',
            items: [
              {
                name: 'Sarah M.',
                role: 'Acheteuse vérifiée',
                text: "Ma peau n'a jamais été aussi hydratée. Le sérum agit en quelques jours et tient ses promesses. J'ai recommandé à toutes mes amies.",
                rating: 5,
              },
              {
                name: 'Amira B.',
                role: 'Cliente fidèle',
                text: "La crème solaire est incroyable — ultra-légère, sans film gras, et ma peau est protégée toute la journée. Je ne peux plus m'en passer.",
                rating: 5,
              },
              {
                name: 'Leila K.',
                role: 'Première commande',
                text: "Livraison ultra-rapide, produit bien emballé et exactement tel que décrit. Le fond de teint couvre parfaitement sans étouffer la peau.",
                rating: 5,
              },
            ],
          },
        },
        // ── 10. Newsletter (community capture — last touchpoint) ──────────────
        {
          id: 'beauty-home-newsletter',
          type: 'newsletter',
          visible: true,
          props: {
            title: 'Rejoignez Notre Communauté Beauté',
            subtitle: 'Recevez en avant-première nos lancements, conseils beauté et offres exclusives.',
            placeholder: 'Votre adresse email',
            buttonText: "S'inscrire",
            backgroundColor: '',
          },
        },
      ],

      shop: [
        {
          id: 'beauty-shop-announce',
          type: 'announcement-bar',
          visible: true,
          props: {
            text: 'Livraison gratuite dès 100 TND • Retours faciles sous 7 jours',
            backgroundColor: '',
          },
        },
        {
          id: 'beauty-shop-hero',
          type: 'hero',
          visible: true,
          props: {},
        },
        {
          id: 'beauty-shop-featured',
          type: 'featured-collection',
          visible: true,
          props: {
            title: 'Nos Coups de Cœur',
            limit: 4,
          },
        },
        {
          id: 'beauty-shop-grid',
          type: 'product-grid',
          visible: true,
          props: {
            title: 'Tous les Produits',
            subtitle: 'Soins et beauté pour tous les types de peau',
            limit: 24,
            columns: 4,
            anchorId: 'products',
          },
        },
      ],

      product: [
        {
          id: 'beauty-pdp-details',
          type: 'product-details',
          visible: true,
          props: {
            highlights: [
              'Formule dermatologiquement testée',
              'Sans parabènes, sans perturbateurs endocriniens',
              'Résultats visibles dès 14 jours d\'utilisation',
            ],
            trustShipping: 'Livraison sous 24-48h partout en Tunisie.',
            trustReturns: 'Retours acceptés 7 jours après réception.',
            trustPayment: 'Paiement à la livraison, D17 et Konnect acceptés.',
          },
        },
        {
          id: 'beauty-pdp-tabs',
          type: 'product-tabs',
          visible: true,
          props: {
            tabs: [
              {
                label: 'Description',
                content: 'Ce produit a été développé en collaboration avec des dermatologues pour offrir les meilleurs résultats possibles sur tous les types de peau.',
              },
              {
                label: 'Ingrédients',
                content: 'Aqua, Glycerin, Niacinamide 10%, Zinc PCA 1%, Sodium Hyaluronate, Panthenol, Tocopherol (Vitamine E), Allantoin. Sans alcool, parabènes, ni parfums artificiels.',
              },
              {
                label: 'Mode d\'emploi',
                content: 'Appliquer matin et soir sur une peau propre et sèche. Masser délicatement jusqu\'à absorption complète. Pour un résultat optimal, utiliser en cure de 30 jours.',
              },
              {
                label: 'Livraison & Retours',
                content: 'Livraison standard 24-48h en Tunisie. Retours acceptés sous 7 jours pour tout produit non ouvert dans son emballage d\'origine.',
              },
            ],
          },
        },
        {
          id: 'beauty-pdp-related',
          type: 'related-products',
          visible: true,
          props: {
            title: 'Complétez Votre Routine',
            limit: 4,
          },
        },
      ],

      category: [
        {
          id: 'beauty-cat-grid',
          type: 'category-grid',
          visible: true,
          props: {},
        },
      ],

      cart: [
        {
          id: 'beauty-cart-lines',
          type: 'cart-lines',
          visible: true,
          props: {},
        },
        {
          id: 'beauty-cart-summary',
          type: 'cart-summary',
          visible: true,
          props: {},
        },
      ],

      checkout: [
        {
          id: 'beauty-checkout-form',
          type: 'checkout-form',
          visible: true,
          props: {},
        },
      ],

      faq: [
        {
          id: 'beauty-faq',
          type: 'faq-accordion',
          visible: true,
          props: {
            title: 'Questions Fréquentes',
            subtitle: 'Tout ce que vous devez savoir avant de commander',
            items: [
              {
                question: 'Vos produits sont-ils testés sur les animaux ?',
                answer: 'Non, tous nos produits sont 100% cruelty-free et ne sont jamais testés sur les animaux.',
              },
              {
                question: 'Pour quels types de peau sont faits vos produits ?',
                answer: 'Nous proposons des produits adaptés à tous les types de peau. Chaque fiche produit précise les types de peau recommandés.',
              },
              {
                question: 'Quels sont les délais de livraison ?',
                answer: 'La livraison standard prend 24 à 48 heures ouvrables partout en Tunisie. Des options express sont disponibles au moment du paiement.',
              },
              {
                question: 'Puis-je retourner un produit ?',
                answer: "Oui, vous disposez de 7 jours après réception pour retourner tout produit non ouvert dans son emballage d'origine. Contactez-nous pour initier le retour.",
              },
              {
                question: 'Les ingrédients sont-ils naturels ?',
                answer: "Nous utilisons un mélange d'actifs naturels et d'ingrédients scientifiquement prouvés. La liste complète figure sur chaque fiche produit.",
              },
            ],
          },
        },
      ],

      about: [
        {
          id: 'beauty-about-banner',
          type: 'image-banner',
          visible: true,
          props: {
            image: '/templates/local-webp/bty-about.webp',
            title: 'Notre Histoire',
            subtitle: "Nés d'une passion pour la beauté authentique et le soin responsable.",
            ctaLabel: 'Voir notre boutique',
            ctaLink: '/shop',
          },
        },
        {
          id: 'beauty-about-story',
          type: 'rich-text',
          visible: true,
          props: {
            title: 'Une marque née d\'une conviction',
            text: "Nous avons fondé cette marque avec une idée simple : que chaque personne mérite des produits de soin efficaces, honnêtes et accessibles.\n\nTous nos produits sont formulés en partenariat avec des dermatologues, fabriqués dans des laboratoires certifiés, et testés avant chaque lancement.\n\nNous croyons que la beauté ne devrait pas être compliquée — ni dans la routine, ni dans les ingrédients, ni dans les prix.",
          },
        },
        {
          id: 'beauty-about-trust',
          type: 'trust-strip',
          visible: true,
          props: {},
        },
      ],

      contact: [
        {
          id: 'beauty-contact-info',
          type: 'rich-text',
          visible: true,
          props: {
            title: 'Nous Contacter',
            text: "Notre équipe est disponible 7j/7 pour répondre à toutes vos questions.\n\nEmail : contact@votreboutique.tn\nTéléphone / WhatsApp : +216 XX XXX XXX\nHoraires : Lun–Sam, 9h–21h\n\nNous répondons généralement dans un délai de 2 heures pendant les heures ouvrables.",
          },
        },
        {
          id: 'beauty-contact-faq-teaser',
          type: 'promo-banner',
          visible: true,
          props: {
            text: 'Consultez notre FAQ pour des réponses instantanées aux questions les plus courantes.',
            ctaLabel: 'Voir la FAQ',
            ctaLink: '/faq',
            backgroundColor: '',
            textColor: '',
          },
        },
      ],
    },
  },
}

// ─── FASHION ─────────────────────────────────────────────────────────────────

const fashionTemplate: StoreTemplate = {
  id: 'fashion',
  name: 'Mode & Prêt-à-Porter',
  description: 'Template éditorial pour boutiques de mode, vêtements et accessoires.',
  niche: 'Mode',
  thumbnail: '/templates/fashion-v2-gen/fv2-gen-thumb.webp',
  layout: {
    theme: {
      id: 'luxury',
      primary: '#0a0a0a',
      secondary: '#f5f5f5',
      backgroundColor: '#ffffff',
      font: 'Inter',
      radius: '0.25rem',
      ui: {
        productCard: 'bold',
        buttons: 'secondary',
        spacing: 'normal',
        headingStyle: 'bold',
        font: 'sans',
        density: 'comfortable',
        mode: 'light',
        radius: 'none',
      },
    } as any,
    header: {
      props: {
        storeName: null,
        menuItems: [
          { label: 'Accueil', link: '/' },
          { label: 'Boutique', link: '/shop' },
          { label: 'Nouveautés', link: '/category/nouveautes' },
          { label: 'Soldes', link: '/category/soldes' },
          { label: 'À Propos', link: '/about' },
          { label: 'Contact', link: '/contact' },
        ],
        showSearch: true,
        showCart: true,
        sticky: true,
        variant: 'default',
      },
    },
    footer: {
      props: {
        storeName: true,
        description: 'Mode contemporaine conçue pour la femme et l\'homme modernes — qualité premium, style intemporel.',
        copyright: `© ${new Date().getFullYear()} Votre Boutique. Tous droits réservés.`,
        columns: [
          {
            title: 'Collections',
            links: [
              { label: 'Nouveautés', link: '/category/nouveautes' },
              { label: 'Femme', link: '/category/femme' },
              { label: 'Homme', link: '/category/homme' },
              { label: 'Accessoires', link: '/category/accessoires' },
              { label: 'Soldes', link: '/category/soldes' },
            ],
          },
          {
            title: 'Aide',
            links: [
              { label: 'Guide des tailles', link: '/faq' },
              { label: 'Livraison', link: '/policy/shipping' },
              { label: 'Retours', link: '/policy/returns' },
              { label: 'FAQ', link: '/faq' },
            ],
          },
          {
            title: 'La Marque',
            links: [
              { label: 'Notre Histoire', link: '/about' },
              { label: 'Contact', link: '/contact' },
            ],
          },
        ],
        socialLinks: {
          instagram: 'https://instagram.com',
          facebook: 'https://facebook.com',
          tiktok: 'https://tiktok.com',
        },
        showPaymentIcons: true,
      },
    },
    pages: {
      home: [
        // ── 1. Announce bar ──────────────────────────────────────────────────
        {
          id: 'fashion-home-announce',
          type: 'announcement-bar',
          visible: true,
          props: {
            text: 'Nouvelle collection disponible — Livraison gratuite dès 150 TND',
            backgroundColor: '#0a0a0a',
          },
        },
        // ── 2. Full-width editorial banner ───────────────────────────────────
        // Fashion = aspirational imagery first, before any product grid
        {
          id: 'fashion-home-banner',
          type: 'image-banner',
          visible: true,
          props: {
            image: '/templates/fashion-v2-gen/fv2-gen-collage-a.webp',
            title: 'Nouvelle Saison',
            subtitle: "Des pièces intemporelles pensées pour s'adapter à chaque moment de votre journée.",
            ctaLabel: 'Explorer la collection',
            ctaLink: '/shop',
            overlayOpacity: 35,
            minHeight: 'xl',
            textAlign: 'center',
          },
        },
        // ── 3. Category strip — browse by collection before editorial stories
        {
          id: 'fashion-home-cats',
          type: 'category-strip',
          visible: true,
          props: {},
        },
        // ── 4. Lookbook story — editorial brands lead with imagery ───────────
        {
          id: 'fashion-home-lookbook',
          type: 'image-with-text',
          visible: true,
          props: {
            image: '/templates/fashion-v2-gen/fv2-gen-collage-b.webp',
            imageAlt: 'Modèle portant la nouvelle collection capsule',
            title: 'La Collection Capsule',
            text: "Chaque pièce de notre collection capsule est conçue pour traverser les saisons. Des matières nobles, des coupes travaillées et une finition soignée — parce que votre garde-robe mérite le meilleur.",
            ctaLabel: 'Voir la collection',
            ctaLink: '/shop',
            layout: 'image-left',
            imageAspect: 'portrait',
          },
        },
        // ── 5. Promo banner (seasonal scarcity before generic reassurance)
        {
          id: 'fashion-home-promo',
          type: 'promo-banner',
          visible: true,
          props: {
            text: 'Soldes — Jusqu\'à -40% sur une sélection de pièces',
            highlight: 'Stock limité.',
            backgroundColor: '',
            textColor: '',
            ctaLabel: 'Voir les soldes',
            ctaLink: '/category/soldes',
            showIcon: true,
          },
        },
        // ── 6. Trust strip (quick reassurance before browsing) ───────────────
        {
          id: 'fashion-home-trust',
          type: 'trust-strip',
          visible: true,
          props: {},
        },
        // ── 7. Hand-picked best-sellers ───────────────────────────────────────
        {
          id: 'fashion-home-featured',
          type: 'featured-collection',
          visible: true,
          props: {
            title: 'Les Incontournables',
            subtitle: 'Nos pièces les plus portées cette saison',
            limit: 8,
            columns: 4,
            viewAllLabel: 'Voir tout',
            viewAllLink: '/shop',
          },
        },
        // ── 8. Full catalog grid ─────────────────────────────────────────────
        {
          id: 'fashion-home-grid',
          type: 'product-grid',
          visible: true,
          props: {
            title: 'Toute la Boutique',
            subtitle: 'Femme, homme et accessoires — découvrez tout le catalogue.',
            limit: 12,
            columns: 4,
            anchorId: 'boutique',
          },
        },
        // ── 9. Testimonials ──────────────────────────────────────────────────
        {
          id: 'fashion-home-testimonials',
          type: 'testimonials',
          visible: true,
          props: {
            title: 'Ils Nous Font Confiance',
            subtitle: 'Avis clients vérifiés',
            items: [
              {
                name: 'Yasmine T.',
                role: 'Acheteuse vérifiée',
                text: "La qualité de cette veste est impressionnante. Les coutures sont parfaites et la coupe est exactement celle des photos. Je commande chaque saison.",
                rating: 5,
              },
              {
                name: 'Omar S.',
                role: 'Client régulier',
                text: "J'achète ici depuis 2 ans. Jamais déçu. La nouvelle collection est particulièrement réussie — des pièces facilement portables au quotidien.",
                rating: 5,
              },
              {
                name: 'Rania B.',
                role: 'Première commande',
                text: "Excellent rapport qualité-prix. Le tissu est épais et solide, les couleurs sont fidèles aux photos. Livraison en 24h — parfait !",
                rating: 4,
              },
            ],
          },
        },
        // ── 10. Newsletter ───────────────────────────────────────────────────
        {
          id: 'fashion-home-newsletter',
          type: 'newsletter',
          visible: true,
          props: {
            title: 'Soyez les Premiers Informés',
            subtitle: "Accédez en avant-première aux nouvelles collections et offres exclusives réservées aux abonnés.",
            placeholder: 'Votre adresse email',
            buttonText: "Je m'abonne",
            backgroundColor: '',
          },
        },
      ],

      shop: [
        {
          id: 'fashion-shop-announce',
          type: 'announcement-bar',
          visible: true,
          props: {
            text: 'Livraison gratuite dès 150 TND • Échanges faciles',
            backgroundColor: '',
          },
        },
        {
          id: 'fashion-shop-hero',
          type: 'hero',
          visible: true,
          props: {},
        },
        {
          id: 'fashion-shop-featured',
          type: 'featured-collection',
          visible: true,
          props: {
            title: 'Sélection du Moment',
            limit: 4,
          },
        },
        {
          id: 'fashion-shop-grid',
          type: 'product-grid',
          visible: true,
          props: {
            title: 'Tous les Articles',
            subtitle: 'Découvrez l\'intégralité de notre catalogue',
            limit: 24,
            columns: 4,
            anchorId: 'products',
          },
        },
      ],

      product: [
        {
          id: 'fashion-pdp-details',
          type: 'product-details',
          visible: true,
          props: {
            highlights: [
              'Matières premium sélectionnées avec soin',
              'Coupe ajustée pour chaque morphologie',
              'Finitions et coutures de qualité supérieure',
            ],
            trustShipping: 'Livraison 24-48h partout en Tunisie.',
            trustReturns: 'Retours et échanges sous 7 jours, article non porté avec étiquette.',
            trustPayment: 'Paiement à la livraison ou en ligne — 100% sécurisé.',
          },
        },
        {
          id: 'fashion-pdp-tabs',
          type: 'product-tabs',
          visible: true,
          props: {
            tabs: [
              {
                label: 'Description',
                content: 'Pièce conçue pour une polyvalence maximale — du bureau à la soirée. Finitions soignées, coupe travaillée, matière respirante.',
              },
              {
                label: 'Matières & Entretien',
                content: 'Composition disponible sur l\'étiquette du vêtement. Lavage recommandé : 30°C, cycle délicat. Ne pas tumble-dry. Repassage à chaleur modérée.',
              },
              {
                label: 'Guide des Tailles',
                content: 'Consultez notre guide des tailles avant de commander. En cas de doute entre deux tailles, nous recommandons de prendre la taille supérieure pour un fit ample, ou la taille inférieure pour un fit ajusté.',
              },
              {
                label: 'Livraison & Retours',
                content: 'Livraison standard 24-48h. Retours acceptés sous 7 jours pour articles non portés avec étiquettes. Contactez-nous pour initier un retour.',
              },
            ],
          },
        },
        {
          id: 'fashion-pdp-related',
          type: 'related-products',
          visible: true,
          props: {
            title: 'Vous Aimerez Aussi',
            limit: 4,
          },
        },
      ],

      category: [
        {
          id: 'fashion-cat-announce',
          type: 'announcement-bar',
          visible: true,
          props: {
            text: 'Nouvelle saison · Retours 7 jours sur articles avec étiquettes',
            backgroundColor: '#0a0a0a',
          },
        },
        {
          id: 'fashion-cat-lookbook',
          type: 'image-banner',
          visible: true,
          props: {
            image: '/templates/fashion-v2-gen/fv2-gen-shop-hero.webp',
            title: 'La collection en contexte',
            subtitle: 'Styling, matière et tombé — visuels pleine largeur pour cette catégorie.',
            ctaLabel: 'Voir toute la boutique',
            ctaLink: '/shop',
            overlayOpacity: 36,
            minHeight: 'md',
            textAlign: 'left',
          },
        },
        {
          id: 'fashion-cat-strip',
          type: 'category-strip',
          visible: true,
          props: {},
        },
        {
          id: 'fashion-cat-grid',
          type: 'category-grid',
          visible: true,
          props: {},
        },
      ],

      cart: [
        {
          id: 'fashion-cart-lines',
          type: 'cart-lines',
          visible: true,
          props: {},
        },
        {
          id: 'fashion-cart-summary',
          type: 'cart-summary',
          visible: true,
          props: {},
        },
      ],

      checkout: [
        {
          id: 'fashion-checkout-form',
          type: 'checkout-form',
          visible: true,
          props: {},
        },
      ],

      faq: [
        {
          id: 'fashion-faq',
          type: 'faq-accordion',
          visible: true,
          props: {
            title: 'Guide & Questions',
            subtitle: 'Tout ce que vous devez savoir sur nos produits et notre service',
            items: [
              {
                question: 'Comment trouver ma taille ?',
                answer: "Chaque fiche produit inclut un guide des tailles avec les mesures exactes. En cas de doute, n'hésitez pas à nous contacter via WhatsApp pour un conseil personnalisé.",
              },
              {
                question: 'Quelles matières utilisez-vous ?',
                answer: 'Nous sélectionnons des matières premium : coton, lin, mélanges textiles de qualité. La composition est précisée sur l\'étiquette de chaque article.',
              },
              {
                question: 'Puis-je échanger un article ?',
                answer: "Oui, les échanges de taille sont acceptés sous 7 jours. L'article doit être non porté, avec étiquettes. Contactez notre service client pour organiser l'échange.",
              },
              {
                question: 'Proposez-vous des retouches ?',
                answer: "Nous ne proposons pas de service de retouche pour le moment. Notre guide des tailles est très détaillé pour vous aider à choisir la bonne coupe.",
              },
              {
                question: 'Combien de temps prend la livraison ?',
                answer: 'Livraison standard sous 24-48h ouvrables partout en Tunisie. Vous recevrez un SMS avec le suivi une fois votre commande expédiée.',
              },
            ],
          },
        },
      ],

      about: [
        {
          id: 'fashion-about-banner',
          type: 'image-banner',
          visible: true,
          props: {
            image: '/templates/fashion-v2-gen/fv2-gen-about-vision.webp',
            title: 'Notre Philosophie',
            subtitle: 'La mode accessible à tous, sans compromis sur la qualité.',
            ctaLabel: 'Voir la boutique',
            ctaLink: '/shop',
          },
        },
        {
          id: 'fashion-about-story',
          type: 'rich-text',
          visible: true,
          props: {
            title: 'Moins, mais mieux',
            text: "Nous avons créé cette marque avec une conviction simple : il est possible de proposer des vêtements de qualité à des prix justes.\n\nChaque pièce est sélectionnée ou conçue en pensant durabilité et style. Nous travaillons avec des ateliers qui partagent nos valeurs de qualité et d'honnêteté.\n\nNous ne croyons pas aux collections éphémères qui remplissent les armoires pour être oubliées. Nous croyons en des pièces que l'on garde, qu'on répare, qu'on aime.",
          },
        },
        {
          id: 'fashion-about-trust',
          type: 'trust-strip',
          visible: true,
          props: {},
        },
      ],

      contact: [
        {
          id: 'fashion-contact-info',
          type: 'rich-text',
          visible: true,
          props: {
            title: 'Contactez-Nous',
            text: "Nous sommes disponibles 7 jours sur 7 pour vous accompagner dans vos achats.\n\nEmail : contact@votreboutique.tn\nWhatsApp : +216 XX XXX XXX\nHoraires : 9h–21h tous les jours\n\nPour les questions de tailles, envoyez-nous une photo de votre morphologie et nous vous conseillerons personnellement.",
          },
        },
      ],
    },
  },
}

// ─── ELECTRONICS ─────────────────────────────────────────────────────────────

const electronicsTemplate: StoreTemplate = {
  id: 'electronics',
  name: 'High-Tech & Électronique',
  description: 'Template professionnel pour boutiques high-tech, smartphones et accessoires.',
  niche: 'Électronique',
  thumbnail: '/templates/local-webp/elx-thumbnail.webp',
  layout: {
    theme: {
      id: 'dawn',
      primary: 'hsl(217 91% 50%)',
      secondary: 'hsl(214 32% 91%)',
      backgroundColor: '#f8fafc',
      font: 'Inter',
      radius: '0.5rem',
      ui: {
        productCard: 'minimal',
        buttons: 'ghost',
        spacing: 'airy',
        headingStyle: 'clean',
        font: 'sans',
        density: 'tight',
        mode: 'light',
        radius: 'md',
      },
    } as any,
    header: {
      props: {
        storeName: null,
        menuItems: [
          { label: 'Accueil', link: '/' },
          { label: 'Boutique', link: '/shop' },
          { label: 'Smartphones', link: '/category/smartphones' },
          { label: 'Laptops & tablettes', link: '/category/laptops-tablets' },
          { label: 'FAQ', link: '/faq' },
          { label: 'Contact', link: '/contact' },
        ],
        showSearch: true,
        showCart: true,
        sticky: true,
        variant: 'default',
        promoText: '🔧 SAV 7j/7 · Garantie incluse · Compatible 220V/50Hz',
        promoColor: '#0f172a',
      },
    },
    footer: {
      props: {
        storeName: true,
        description: 'Appareils et accessoires high-tech soigneusement sélectionnés. Garantie locale, SAV tunisien et livraison 24-48h inclus.',
        copyright: `© ${new Date().getFullYear()} Votre Boutique. Tous droits réservés.`,
        columns: [
          {
            title: 'Produits',
            links: [
              { label: 'Tous les produits', link: '/shop' },
              { label: 'Smartphones', link: '/category/smartphones' },
              { label: 'Laptops & tablettes', link: '/category/laptops-tablets' },
              { label: 'Audio', link: '/category/audio' },
              { label: 'Smart home', link: '/category/smart-home' },
            ],
          },
          {
            title: 'Support',
            links: [
              { label: 'FAQ', link: '/faq' },
              { label: 'Garantie', link: '/policy/returns' },
              { label: 'Livraison', link: '/policy/shipping' },
              { label: 'Contact SAV', link: '/contact' },
            ],
          },
          {
            title: 'Entreprise',
            links: [
              { label: 'À Propos', link: '/about' },
              { label: 'Contact', link: '/contact' },
            ],
          },
        ],
        socialLinks: {
          facebook: 'https://facebook.com',
          instagram: 'https://instagram.com',
        },
        showPaymentIcons: true,
      },
    },
    pages: {

      // ── HOME — trust → visual → specs (rational electronics buyer flow) ───
      home: [
        {
          id: 'tech-home-announce',
          type: 'announcement-bar',
          visible: true,
          props: {
            text: '🔧 SAV 7j/7 · Garantie incluse · Compatible 220V/50Hz · Livraison 24-48h',
            backgroundColor: '#0f172a',
          },
        },
        // Guarantees before the hero visual — distinct from fashion / decor openers
        {
          id: 'tech-home-trust',
          type: 'trust-strip',
          visible: true,
          props: {},
        },
        {
          id: 'tech-home-banner',
          type: 'image-banner',
          visible: true,
          props: {
            image: '/templates/local-webp/elx-hero-banner.webp',
            title: 'La Tech à Votre Portée',
            subtitle: 'Smartphones, tablettes et accessoires high-tech — sélectionnés pour leurs performances, garantis, livrés sous 24-48h.',
            ctaLabel: 'Voir le catalogue',
            ctaLink: '/shop',
            overlayOpacity: 55,
            minHeight: 'lg',
            textAlign: 'left',
          },
        },
        // compatibility guarantee promo — #1 objection for electronics buyers
        {
          id: 'tech-home-promo',
          type: 'promo-banner',
          visible: true,
          props: {
            text: '✅ Tous nos produits sont testés et compatibles avec le standard électrique tunisien 220V/50Hz.',
            highlight: 'Garantie locale incluse sur chaque appareil.',
            backgroundColor: '#f0f9ff',
            textColor: '#0c4a6e',
            ctaLabel: 'En savoir plus',
            ctaLink: '/faq',
            showIcon: false,
          },
        },
        // category strip POSITION #5 — tech buyers know what they want, nav by type
        {
          id: 'tech-home-cats',
          type: 'category-strip',
          visible: true,
          props: {},
        },
        {
          id: 'tech-home-featured',
          type: 'featured-collection',
          visible: true,
          props: {
            title: 'Top Ventes & Nouveautés',
            subtitle: 'Les appareils les plus demandés — performants, garantis, livrés rapidement',
            limit: 8,
            columns: 4,
            viewAllLabel: 'Voir tout le catalogue',
            viewAllLink: '/shop',
          },
        },
        // mini-FAQ BEFORE the catalog grid — resolves objections, boosts conversion
        {
          id: 'tech-home-faq',
          type: 'faq-accordion',
          visible: true,
          props: {
            title: 'Vos Questions, Nos Réponses',
            subtitle: "Réponses rapides avant d'acheter",
            items: [
              {
                question: 'Les produits sont-ils compatibles avec les prises tunisiennes ?',
                answer: "Oui, tous nos produits sont testés et compatibles avec le standard tunisien 220V/50Hz. Un adaptateur est fourni si nécessaire.",
              },
              {
                question: 'Quelle garantie est incluse ?',
                answer: "Tous nos produits bénéficient d'une garantie minimale de 6 mois. Certains appareils offrent 1 an. Les détails figurent sur chaque fiche produit.",
              },
              {
                question: 'Puis-je tester le produit à la livraison ?',
                answer: "Oui. Lors de la livraison à domicile avec paiement en espèces, notre livreur patientera pendant votre vérification avant que vous régliez.",
              },
              {
                question: 'Les produits sont-ils neufs et originaux ?',
                answer: "Oui, tous nos produits sont neufs, originaux, dans leur emballage d'origine. Nous ne vendons pas de reconditionnés sauf mention explicite.",
              },
            ],
          },
        },
        {
          id: 'tech-home-grid',
          type: 'product-grid',
          visible: true,
          props: {
            title: 'Tout le Catalogue',
            subtitle: 'Smartphones, tablettes, périphériques, câbles et accessoires',
            limit: 12,
            columns: 4,
            anchorId: 'catalogue',
          },
        },
        {
          id: 'tech-home-testimonials',
          type: 'testimonials',
          visible: true,
          props: {
            title: 'Ce Que Disent Nos Clients',
            subtitle: 'Avis vérifiés après achat',
            items: [
              {
                name: 'Ahmed K.',
                role: 'Acheteur vérifié',
                text: "Livraison ultra-rapide et produit impeccablement emballé. Fonctionne parfaitement, exactement comme décrit. J'ai pu le tester avant de payer — parfait.",
                rating: 5,
              },
              {
                name: 'Mohamed B.',
                role: 'Client régulier',
                text: "SAV exemplaire. J'avais un petit problème et c'était réglé en quelques heures via WhatsApp. Je commande uniquement ici pour tout mon matériel tech.",
                rating: 5,
              },
              {
                name: 'Sana L.',
                role: 'Acheteuse vérifiée',
                text: "Qualité bien au-dessus de ce que j'espérais pour ce prix. La garantie incluse m'a rassurée — et je n'en ai même pas eu besoin. Impeccable.",
                rating: 5,
              },
            ],
          },
        },
        {
          id: 'tech-home-story',
          type: 'image-with-text',
          visible: true,
          props: {
            image: '/templates/local-webp/elx-story.webp',
            imageAlt: 'Sélection rigoureuse de produits tech',
            title: 'Chaque produit est sélectionné avec soin',
            text: "Avant d'être proposé sur notre boutique, chaque appareil passe par nos tests de performance, de compatibilité locale et de durabilité. Si ça ne passe pas nos critères, vous ne le verrez jamais.",
            ctaLabel: 'Notre démarche',
            ctaLink: '/about',
            layout: 'image-left',
            imageAspect: 'landscape',
          },
        },
        {
          id: 'tech-home-newsletter',
          type: 'newsletter',
          visible: true,
          props: {
            title: 'Soyez les Premiers Informés',
            subtitle: 'Nouveautés, baisses de prix et offres flash — dans votre boîte mail avant tout le monde.',
            placeholder: 'Votre adresse email',
            buttonText: "S'abonner",
            backgroundColor: '',
          },
        },
      ],

      // ── SHOP — cat strip → spec filter hint → full catalog ────────────────
      shop: [
        {
          id: 'tech-shop-announce',
          type: 'announcement-bar',
          visible: true,
          props: {
            text: '✅ Garantie incluse · 🔧 SAV 7j/7 · Livraison 24-48h avec possibilité de tester avant de payer',
            backgroundColor: '#0f172a',
          },
        },
        {
          id: 'tech-shop-cats',
          type: 'category-strip',
          visible: true,
          props: {},
        },
        {
          id: 'tech-shop-featured',
          type: 'featured-collection',
          visible: true,
          props: {
            title: 'Top Ventes',
            subtitle: 'Les produits les plus commandés cette semaine',
            limit: 4,
            columns: 4,
            viewAllLabel: '',
            viewAllLink: '',
          },
        },
        {
          id: 'tech-shop-grid',
          type: 'product-grid',
          visible: true,
          props: {
            title: 'Tout le Catalogue',
            subtitle: 'Smartphones, tablettes, accessoires et périphériques',
            limit: 24,
            columns: 4,
            anchorId: 'produits',
          },
        },
      ],

      // ── PRODUCT — specs + guarantee tabs + compatibility accessories ────────
      product: [
        {
          id: 'tech-pdp-details',
          type: 'product-details',
          visible: true,
          props: {
            highlights: [
              'Testé et vérifié avant expédition — conforme à la description',
              'Compatible normes électriques tunisiennes 220V/50Hz',
              'Garantie locale incluse — SAV disponible 7j/7 via WhatsApp',
            ],
            trustShipping: 'Expédition soigneuse avec emballage de protection, livraison 24-48h avec signature.',
            trustReturns: 'Produit défectueux à réception ? Échange immédiat sous 7 jours sans discussion.',
            trustPayment: 'Paiement à la livraison (vérification avant paiement), D17, Flouci et Konnect acceptés.',
          },
        },
        {
          id: 'tech-pdp-tabs',
          type: 'product-tabs',
          visible: true,
          props: {
            tabs: [
              {
                label: 'Caractéristiques techniques',
                content: "Consultez les spécifications complètes sur cette fiche : processeur, mémoire, autonomie, connectivité et compatibilité système.",
              },
              {
                label: 'Contenu de la boîte',
                content: "Appareil principal · Chargeur adapté standard tunisien 220V · Câble USB · Manuel d'utilisation (français & arabe) · Carte de garantie.",
              },
              {
                label: 'Garantie & SAV',
                content: "Garantie locale de 6 à 12 mois selon le produit. Pour toute réclamation SAV, contactez-nous via WhatsApp avec votre numéro de commande + une photo. Réponse en moins de 2h.",
              },
              {
                label: 'Livraison & Test avant paiement',
                content: "Emballage sécurisé · Livraison 24-48h avec signature · Paiement à domicile : vérifiez le produit avant de régler · Retour accepté sous 7 jours pour tout article défectueux.",
              },
            ],
          },
        },
        {
          id: 'tech-pdp-related',
          type: 'related-products',
          visible: true,
          props: {
            title: 'Accessoires Compatibles',
            limit: 4,
            columns: 4,
          },
        },
      ],

      // ── CATEGORY ─────────────────────────────────────────────────────────
      category: [
        {
          id: 'tech-cat-announce',
          type: 'announcement-bar',
          visible: true,
          props: {
            text: '✅ Garantie incluse · 🔧 SAV 7j/7 · Compatible 220V/50Hz',
            backgroundColor: '#0f172a',
          },
        },
        {
          id: 'tech-cat-trust',
          type: 'promo-banner',
          visible: true,
          props: {
            text: '✅ Tous les produits de cette catégorie sont neufs, originaux, compatibles 220V/50Hz et garantis.',
            highlight: '',
            backgroundColor: '#f0f9ff',
            textColor: '#0c4a6e',
            ctaLabel: '',
            ctaLink: '',
            showIcon: false,
          },
        },
        {
          id: 'tech-cat-strip',
          type: 'category-strip',
          visible: true,
          props: {},
        },
        {
          id: 'tech-cat-grid',
          type: 'category-grid',
          visible: true,
          props: {},
        },
      ],

      // ── CART — guarantee reminder + spec check prompt + accessory upsell ──
      cart: [
        {
          id: 'tech-cart-announce',
          type: 'announcement-bar',
          visible: true,
          props: {
            text: '✅ Garantie incluse · Livraison 24-48h · Test avant paiement disponible',
            backgroundColor: '#0f172a',
          },
        },
        {
          id: 'tech-cart-lines',
          type: 'cart-lines',
          visible: true,
          props: {},
        },
        {
          id: 'tech-cart-summary',
          type: 'cart-summary',
          visible: true,
          props: {},
        },
        {
          id: 'tech-cart-accessories',
          type: 'featured-collection',
          visible: true,
          props: {
            title: 'Accessoires Recommandés',
            subtitle: 'Protection, câbles et chargeurs compatibles avec votre commande',
            limit: 4,
            columns: 4,
            viewAllLabel: '',
            viewAllLink: '',
          },
        },
      ],

      // ── CHECKOUT — confidence maximized before payment ────────────────────
      checkout: [
        {
          id: 'tech-checkout-announce',
          type: 'announcement-bar',
          visible: true,
          props: {
            text: '🔒 Commande sécurisée · Test avant paiement à domicile · Garantie incluse',
            backgroundColor: '#0f172a',
          },
        },
        {
          id: 'tech-checkout-form',
          type: 'checkout-form',
          visible: true,
          props: {},
        },
      ],

      // ── SEARCH ────────────────────────────────────────────────────────────
      search: [
        {
          id: 'tech-search-announce',
          type: 'announcement-bar',
          visible: true,
          props: {
            text: '✅ Garantie incluse · 🔧 SAV 7j/7 · Livraison 24-48h',
            backgroundColor: '#0f172a',
          },
        },
        {
          id: 'tech-search-results',
          type: 'search-results',
          visible: true,
          props: {},
        },
        {
          id: 'tech-search-fallback',
          type: 'featured-collection',
          visible: true,
          props: {
            title: 'Nos Produits Populaires',
            subtitle: 'Les appareils les plus commandés en ce moment',
            limit: 4,
            columns: 4,
            viewAllLabel: 'Voir tout',
            viewAllLink: '/shop',
          },
        },
      ],

      // ── ACCOUNT ───────────────────────────────────────────────────────────
      account: [
        {
          id: 'tech-account-announce',
          type: 'announcement-bar',
          visible: true,
          props: {
            text: '🔧 SAV disponible 7j/7 · Consultez vos commandes et votre garantie',
            backgroundColor: '#0f172a',
          },
        },
      ],

      // ── ABOUT — rigorous selection, local SAV, tech expertise ─────────────
      about: [
        {
          id: 'tech-about-banner',
          type: 'image-banner',
          visible: true,
          props: {
            image: '/templates/local-webp/elx-hero-banner.webp',
            title: 'La Tech, Sélectionnée avec Rigueur',
            subtitle: 'Chaque produit est testé pour ses performances, sa compatibilité locale et sa durabilité avant de rejoindre notre catalogue.',
            ctaLabel: 'Voir le catalogue',
            ctaLink: '/shop',
            overlayOpacity: 55,
            minHeight: 'lg',
            textAlign: 'center',
          },
        },
        {
          id: 'tech-about-text',
          type: 'rich-text',
          visible: true,
          props: {
            title: 'Notre Mission',
            text: "Nous croyons que chaque tunisien mérite d'avoir accès à la meilleure technologie, à des prix transparents et avec un service après-vente digne de confiance.\n\nChaque produit passe par nos tests de performance, vérification de compatibilité électrique locale (220V/50Hz) et validation de la durabilité. Si ça ne passe pas nos critères, vous ne le verrez jamais.\n\nNotre SAV est basé en Tunisie — vous parlez à de vraies personnes, pas à des bots, et vos problèmes sont résolus en heures, pas en jours.",
          },
        },
        {
          id: 'tech-about-process',
          type: 'image-with-text',
          visible: true,
          props: {
            image: '/templates/local-webp/elx-story.webp',
            imageAlt: 'Test de produits électroniques en laboratoire',
            title: 'Notre Processus de Sélection',
            text: "1. Test de performance en conditions réelles\n2. Vérification de compatibilité 220V/50Hz\n3. Test de durabilité et résistance\n4. Validation de l'authenticité produit\n5. Contrôle de l'emballage et des accessoires\n\nSi le produit échoue à une seule étape, il ne rejoint pas notre catalogue.",
            ctaLabel: 'Voir nos garanties',
            ctaLink: '/faq',
            layout: 'image-right',
            imageAspect: 'landscape',
          },
        },
        {
          id: 'tech-about-trust',
          type: 'trust-strip',
          visible: true,
          props: {},
        },
      ],

      // ── CONTACT — SAV-first, technical support ────────────────────────────
      contact: [
        {
          id: 'tech-contact-info',
          type: 'rich-text',
          visible: true,
          props: {
            title: 'Support & SAV',
            text: "Notre équipe technique est disponible 7j/7.\n\nWhatsApp SAV : +216 XX XXX XXX\nEmail : sav@votreboutique.tn\nHoraires : 9h – 22h, 7 jours sur 7\n\nPour une intervention rapide, envoyez votre numéro de commande + une photo ou vidéo du problème via WhatsApp. Délai de réponse garanti : moins de 2 heures.\n\nEn cas de produit défectueux, nous organisons immédiatement un échange ou remboursement.",
          },
        },
        {
          id: 'tech-contact-faq',
          type: 'promo-banner',
          visible: true,
          props: {
            text: "Avant de nous écrire, consultez notre FAQ — elle répond à 90% des questions techniques et pratiques.",
            ctaLabel: 'Voir la FAQ',
            ctaLink: '/faq',
            backgroundColor: '',
            textColor: '',
            showIcon: false,
          },
        },
      ],

      // ── FAQ — compatibility, guarantee, SAV, test-before-pay, returns ──────
      faq: [
        {
          id: 'tech-faq',
          type: 'faq-accordion',
          visible: true,
          props: {
            title: 'Questions Techniques & Pratiques',
            subtitle: "Toutes les réponses avant d'acheter",
            items: [
              {
                question: 'Les produits sont-ils compatibles avec les prises tunisiennes ?',
                answer: "Oui, tous nos produits sont testés et compatibles avec le standard électrique tunisien 220V/50Hz. Si un adaptateur est nécessaire, il est fourni dans la boîte.",
              },
              {
                question: 'Quelle garantie est incluse avec mon achat ?',
                answer: "Tous nos produits bénéficient d'une garantie locale minimale de 6 mois. Certains appareils offrent jusqu'à 1 an. Les conditions de garantie figurent sur chaque fiche produit.",
              },
              {
                question: 'Proposez-vous un support technique après achat ?',
                answer: "Oui, notre SAV est disponible 7j/7 via WhatsApp et téléphone. Envoyez votre numéro de commande et une description du problème — nous répondons en moins de 2 heures.",
              },
              {
                question: "Que faire si mon produit arrive endommagé ou défectueux ?",
                answer: "Contactez-nous dans les 24h avec des photos. Nous organisons immédiatement un remplacement ou un remboursement complet — sans question.",
              },
              {
                question: 'Puis-je tester le produit avant de payer à la livraison ?',
                answer: "Oui. Pour les commandes avec paiement en espèces à la livraison, notre livreur patientera pendant votre vérification de l'état et du fonctionnement du produit avant que vous régliez.",
              },
              {
                question: 'Les produits sont-ils neufs et originaux ?',
                answer: "Oui, tous nos produits sont neufs, originaux, dans leur emballage d'origine avec tous les accessoires. Nous ne vendons pas de reconditionnés sauf mention explicite et prix différencié.",
              },
              {
                question: 'Comment se passe la livraison ?',
                answer: "Livraison sous 24-48h ouvrables partout en Tunisie. Emballage de protection soigné. Vous recevrez un SMS de suivi dès l'expédition.",
              },
              {
                question: "Comment retourner un produit ?",
                answer: "Les retours sont acceptés sous 7 jours pour tout produit défectueux avec son emballage d'origine. Contactez-nous via WhatsApp pour lancer la procédure.",
              },
            ],
          },
        },
      ],

      // ── POLICY — SHIPPING ─────────────────────────────────────────────────
      'policy-shipping': [
        {
          id: 'tech-policy-shipping',
          type: 'rich-text',
          visible: true,
          props: {
            title: 'Politique de Livraison',
            text: "Délais\nLivraison sous 24-48h ouvrables partout en Tunisie dès validation de commande.\n\nEmballage\nChaque appareil est emballé dans une boîte de protection adaptée pour éviter tout choc pendant le transport.\n\nSignature\nUne signature est requise à la réception. Si vous êtes absent, un avis de passage est laissé avec un numéro de contact.\n\nTest avant paiement\nPour les commandes avec paiement à la livraison en espèces, vous pouvez vérifier le produit avant de régler.\n\nFrais de livraison\nLivraison gratuite dès 200 TND d'achat. Des frais fixes s'appliquent en dessous selon la zone.",
          },
        },
      ],

      // ── POLICY — RETURNS ──────────────────────────────────────────────────
      'policy-returns': [
        {
          id: 'tech-policy-returns',
          type: 'rich-text',
          visible: true,
          props: {
            title: 'Politique de Garantie & Retours',
            text: "Garantie\nTous nos produits sont garantis localement — minimum 6 mois, jusqu'à 1 an selon l'appareil. Les conditions figurent sur chaque fiche produit.\n\nRetours\nLes retours sont acceptés sous 7 jours pour tout produit défectueux, dans son emballage d'origine avec tous les accessoires.\n\nComment procéder\nContactez notre SAV via WhatsApp (+216 XX XXX XXX) avec votre numéro de commande et des photos du problème. Nous organisons immédiatement le retour et le remplacement ou remboursement.\n\nRemboursements\nLe remboursement est effectué dans les 48h après réception et vérification du retour.\n\nProduits non retournables\nLes logiciels, cartes SIM et accessoires consommables ne sont pas retournables une fois ouverts.",
          },
        },
      ],
    },
  },
}

// ─── GENERAL STORE ────────────────────────────────────────────────────────────

const generalTemplate: StoreTemplate = {
  id: 'general',
  name: 'Boutique Généraliste',
  description: 'Template polyvalent pour boutiques multi-catégories, cadeaux et produits du quotidien.',
  niche: 'Général',
  thumbnail: '/templates/local-webp/promo-packs.webp',
  layout: {
    theme: {
      id: 'summer-fresh',
      primary: 'hsl(34 97% 46%)',
      secondary: 'hsl(34 30% 96%)',
      backgroundColor: '#ffffff',
      font: 'Outfit',
      radius: '1rem',
      ui: {
        productCard: 'default',
        buttons: 'primary',
        spacing: 'normal',
        headingStyle: 'clean',
        font: 'sans',
        density: 'comfortable',
        mode: 'light',
        radius: 'xl',
      },
    } as any,
    header: {
      props: {
        storeName: null,
        menuItems: [
          { label: 'Accueil', link: '/' },
          { label: 'Boutique', link: '/shop' },
          { label: 'Promotions', link: '/category/promotions' },
          { label: 'FAQ', link: '/faq' },
          { label: 'À Propos', link: '/about' },
          { label: 'Contact', link: '/contact' },
        ],
        showSearch: true,
        showCart: true,
        sticky: true,
        variant: 'default',
      },
    },
    footer: {
      props: {
        storeName: true,
        description: 'Des produits de qualité sélectionnés pour vous — livraison rapide, paiement à la livraison, retours faciles.',
        copyright: `© ${new Date().getFullYear()} Votre Boutique. Tous droits réservés.`,
        columns: [
          {
            title: 'Boutique',
            links: [
              { label: 'Tous les produits', link: '/shop' },
              { label: 'Promotions', link: '/category/promotions' },
              { label: 'Nouveautés', link: '/category/nouveautes' },
            ],
          },
          {
            title: 'Service Client',
            links: [
              { label: 'FAQ', link: '/faq' },
              { label: 'Livraison', link: '/policy/shipping' },
              { label: 'Retours', link: '/policy/returns' },
              { label: 'Contact', link: '/contact' },
            ],
          },
          {
            title: 'À Propos',
            links: [
              { label: 'Notre Histoire', link: '/about' },
            ],
          },
        ],
        socialLinks: {
          facebook: 'https://facebook.com',
          instagram: 'https://instagram.com',
          tiktok: 'https://tiktok.com',
        },
        showPaymentIcons: true,
      },
    },
    pages: {
      home: [
        // ── 1. Announce bar ──────────────────────────────────────────────────
        {
          id: 'general-home-announce',
          type: 'announcement-bar',
          visible: true,
          props: {
            text: '🚚 Livraison gratuite dès 100 TND • Paiement à la livraison • Retours 7 jours',
            backgroundColor: '',
          },
        },
        // ── 2. Video first — motion merchandising before the static hero
        {
          id: 'general-home-video',
          type: 'video-block',
          visible: true,
          props: {
            videoUrl: 'https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4',
            autoplay: false,
            muted: true,
            loop: true,
            overlayText: 'Découvrez la boutique en mouvement',
            ctaLabel: 'Voir les produits',
          },
        },
        // ── 3. Hero (split layout — works well for any niche) ────────────────
        {
          id: 'general-home-hero',
          type: 'hero',
          visible: true,
          props: {
            headline: 'Tout ce dont vous avez besoin',
            subtitle: 'Des produits de qualité, sélectionnés avec soin — livrés rapidement partout en Tunisie.',
            ctaLabel: 'Voir la boutique',
            anchorId: 'produits',
            backgroundImage: '/templates/local-webp/elx-hero-banner.webp',
            layout: 'centered',
            overlayOpacity: 55,
            textColor: '#ffffff',
          },
        },
        // ── 3. Trust strip ───────────────────────────────────────────────────
        {
          id: 'general-home-trust',
          type: 'trust-strip',
          visible: true,
          props: {},
        },
        {
          id: 'general-home-quickview',
          type: 'quick-view',
          visible: true,
          props: { enabled: true },
        },
        // ── 4. Category grid — CRITICAL for multi-category stores ─────────────
        // Without this, visitors don't know what the store sells
        // This is the most important structural addition for the general template
        {
          id: 'general-home-cats',
          type: 'category-grid',
          visible: true,
          props: {},
        },
        // ── 5. Best-sellers ──────────────────────────────────────────────────
        {
          id: 'general-home-featured',
          type: 'featured-collection',
          visible: true,
          props: {
            title: 'Nos Best-Sellers',
            subtitle: 'Les produits les plus appréciés par nos clients',
            limit: 8,
            columns: 4,
            viewAllLabel: 'Voir tout',
            viewAllLink: '/shop',
          },
        },
        // ── 5b. Product carousel + bento + gallery + journal + logos (visual merchandising stack) ─
        {
          id: 'general-home-carousel',
          type: 'product-carousel',
          visible: true,
          props: {
            title: 'En ce moment',
            subtitle: 'Glissez pour parcourir — pensé pour le mobile comme les vitrines premium.',
            limit: 12,
            autoplay: true,
            showArrows: true,
          },
        },
        {
          id: 'general-home-promo-bento',
          type: 'promo-grid',
          visible: true,
          props: {},
        },
        {
          id: 'general-home-gallery',
          type: 'image-gallery',
          visible: true,
          props: {
            layout: 'masonry',
            columns: 3,
            images: [
              '/templates/local-webp/promo-new.webp',
              '/templates/local-webp/promo-winter.webp',
              '/templates/local-webp/promo-members.webp',
              '/templates/local-webp/pack-bureau.webp',
              '/templates/local-webp/elx-thumbnail.webp',
            ],
          },
        },
        {
          id: 'general-home-blog',
          type: 'blog-grid',
          visible: true,
          props: {
            title: 'Le journal',
            subtitle: 'Conseils, coulisses et actus boutique.',
            columns: 3,
            posts: [
              {
                title: 'Comment nous préparons chaque commande',
                image: '/templates/local-webp/bty-about.webp',
                excerpt: 'Contrôle qualité, emballage soigné et remise au transporteur — étape par étape.',
                link: '/about',
              },
              {
                title: 'Les couleurs de la saison',
                image: '/templates/dropship-velocity-v1/promo-home-rangements-800.webp',
                excerpt: 'Tons chauds, matières douces et idées looks pour composer votre panier.',
                link: '/shop',
              },
              {
                title: 'Livraison & paiement à la livraison',
                image: '/templates/dropship-velocity-v1/promo-skincare-marble-800.webp',
                excerpt: 'Zones desservies, délais moyens et ce qu’il faut savoir avant de commander.',
                link: '/faq',
              },
            ],
          },
        },
        {
          id: 'general-home-logos',
          type: 'logo-carousel',
          visible: true,
          props: {
            autoplay: true,
            duration: 22,
            logos: [
              '/templates/premium-cosmetics-v1/logo-1.webp',
              '/templates/premium-cosmetics-v1/logo-2.webp',
              '/templates/premium-cosmetics-v1/logo-3.webp',
              '/templates/premium-cosmetics-v1/logo-4.webp',
            ],
          },
        },
        // ── 6. Promo banner (payment reassurance — key for first-time buyers) ─
        {
          id: 'general-home-promo',
          type: 'promo-banner',
          visible: true,
          props: {
            text: 'Paiement à la livraison disponible partout en Tunisie — commandez en toute confiance.',
            highlight: 'Zéro risque.',
            backgroundColor: '',
            textColor: '',
            ctaLabel: 'Commander maintenant',
            ctaLink: '/shop',
            showIcon: true,
          },
        },
        // ── 7. Brand story (builds trust for unknown brands) ──────────────────
        {
          id: 'general-home-story',
          type: 'image-with-text',
          visible: true,
          props: {
            image: '/templates/premium-electronics-v1/shop-banner-800.webp',
            imageAlt: 'Notre boutique et notre équipe',
            title: 'Une Boutique de Confiance',
            text: "Nous sélectionnons chaque produit avec soin, en nous assurant qu'il répond à vos attentes en termes de qualité et de prix.\n\nChaque commande est vérifiée avant expédition. Votre satisfaction est notre priorité — et si quelque chose ne va pas, notre équipe est disponible 7j/7 pour arranger les choses.",
            ctaLabel: 'Découvrir la boutique',
            ctaLink: '/shop',
            layout: 'image-left',
            imageAspect: 'square',
          },
        },
        // ── 8. Full catalog grid ─────────────────────────────────────────────
        {
          id: 'general-home-grid',
          type: 'product-grid',
          visible: true,
          props: {
            title: 'Tous Nos Produits',
            subtitle: 'Un catalogue varié, des produits soigneusement sélectionnés.',
            limit: 12,
            columns: 4,
            anchorId: 'produits',
          },
        },
        {
          id: 'general-home-shoppable',
          type: 'shoppable-image',
          visible: true,
          props: {
            image: '/templates/premium-electronics-v1/shoppable.webp',
            hotspots: [],
          },
        },
        {
          id: 'general-home-bundle',
          type: 'bundle-offer',
          visible: true,
          props: {
            title: 'Offre look complète',
            productIds: [],
            discountLabel: 'Composez votre panier idéal',
          },
        },
        // ── 9. Testimonials ──────────────────────────────────────────────────
        {
          id: 'general-home-testimonials',
          type: 'testimonials',
          visible: true,
          props: {
            title: 'Ils Ont Aimé',
            subtitle: 'Avis clients vérifiés',
            items: [
              {
                name: 'Fatma A.',
                role: 'Acheteuse vérifiée',
                text: "Livraison en 24h, produit exactement comme sur les photos. Qualité nettement supérieure à ce que j'espérais pour ce prix. Je recommande !",
                rating: 5,
              },
              {
                name: 'Karim N.',
                role: 'Client régulier',
                text: "Prix imbattables et emballage soigné. Le produit était parfaitement protégé à la livraison. Service au top, je commande régulièrement.",
                rating: 5,
              },
              {
                name: 'Nadia R.',
                role: 'Première commande',
                text: "J'avais des doutes pour un premier achat en ligne mais tout s'est passé parfaitement. Le checkout est simple, la livraison rapide et le produit conforme.",
                rating: 5,
              },
            ],
          },
        },
        {
          id: 'general-home-newsletter',
          type: 'newsletter',
          visible: true,
          props: {
            title: 'Restez Informé',
            subtitle: 'Recevez nos meilleures offres, promotions et nouveautés directement dans votre boîte mail.',
            placeholder: 'Votre adresse email',
            buttonText: "S'inscrire",
            backgroundColor: '',
          },
        },
        {
          id: 'general-home-sticky-cta',
          type: 'sticky-cta',
          visible: true,
          props: {
            text: 'Une question ? Passez votre commande en quelques clics.',
            buttonLabel: 'Acheter',
            link: '/shop',
            visibleOn: 'mobile',
          },
        },
      ],

      shop: [
        {
          id: 'general-shop-announce',
          type: 'announcement-bar',
          visible: true,
          props: {
            text: 'Paiement à la livraison • Livraison 24-48h • Retours faciles',
            backgroundColor: '',
          },
        },
        {
          id: 'general-shop-filters',
          type: 'filters-sidebar',
          visible: true,
          props: {
            enablePrice: true,
            enableCategory: true,
            enableRating: true,
          },
        },
        {
          id: 'general-shop-hero',
          type: 'hero',
          visible: true,
          props: {},
        },
        {
          id: 'general-shop-featured',
          type: 'featured-collection',
          visible: true,
          props: {
            title: 'Sélection du Moment',
            limit: 4,
          },
        },
        {
          id: 'general-shop-grid',
          type: 'product-grid',
          visible: true,
          props: {
            title: 'Tous les Produits',
            subtitle: 'Notre catalogue complet — filtrez et triez pour trouver votre bonheur.',
            limit: 24,
            columns: 4,
            anchorId: 'products',
          },
        },
        {
          id: 'general-shop-quickview',
          type: 'quick-view',
          visible: true,
          props: { enabled: true },
        },
      ],

      product: [
        {
          id: 'general-pdp-details',
          type: 'product-details',
          visible: true,
          props: {
            highlights: [
              'Qualité vérifiée avant expédition',
              'Emballage soigné et sécurisé',
              'Service client disponible 7j/7',
            ],
            trustShipping: 'Livraison standard 24-48h partout en Tunisie.',
            trustReturns: 'Retours acceptés sous 7 jours en parfait état.',
            trustPayment: 'Paiement à la livraison, D17, Flouci et Konnect acceptés.',
          },
        },
        {
          id: 'general-pdp-tabs',
          type: 'product-tabs',
          visible: true,
          props: {
            tabs: [
              {
                label: 'Description',
                content: 'Produit soigneusement sélectionné pour sa qualité et son rapport qualité-prix. Consultez les spécifications détaillées dans la description ci-dessus.',
              },
              {
                label: 'Dimensions & Poids',
                content: "Les dimensions et le poids exacts sont disponibles dans la description du produit. En cas de doute, contactez-nous avant de commander.",
              },
              {
                label: 'Livraison',
                content: "Livraison standard en 24-48h ouvrables partout en Tunisie. Paiement à la livraison disponible. Vous recevrez un SMS de suivi dès l'expédition.",
              },
              {
                label: 'Retours',
                content: "Retours acceptés sous 7 jours pour tout article dans son état d'origine et son emballage. Contactez-nous pour initier la procédure.",
              },
            ],
          },
        },
        {
          id: 'general-pdp-compare',
          type: 'comparison-table',
          visible: true,
          props: {
            title: 'Comparer les avantages',
            features: ['Livraison 24–48h', 'Paiement à la livraison', 'Retours 7 jours'],
            products: [],
          },
        },
        {
          id: 'general-pdp-related',
          type: 'related-products',
          visible: true,
          props: {
            title: 'Vous Aimerez Aussi',
            limit: 4,
          },
        },
      ],

      category: [
        {
          id: 'general-cat-grid',
          type: 'category-grid',
          visible: true,
          props: {},
        },
      ],

      cart: [
        {
          id: 'general-cart-lines',
          type: 'cart-lines',
          visible: true,
          props: {},
        },
        {
          id: 'general-cart-summary',
          type: 'cart-summary',
          visible: true,
          props: {},
        },
      ],

      checkout: [
        {
          id: 'general-checkout-form',
          type: 'checkout-form',
          visible: true,
          props: {},
        },
      ],

      faq: [
        {
          id: 'general-faq',
          type: 'faq-accordion',
          visible: true,
          props: {
            title: 'Questions Fréquentes',
            subtitle: 'Toutes vos questions, répondues clairement',
            items: [
              {
                question: 'Quels modes de paiement acceptez-vous ?',
                answer: "Nous acceptons le paiement à la livraison (cash), D17, Flouci, Konnect et toutes les principales cartes bancaires tunisiennes.",
              },
              {
                question: 'Combien de temps prend la livraison ?',
                answer: "Les commandes sont traitées dans les 24h. La livraison prend 24 à 48h ouvrables partout en Tunisie. Vous recevrez un SMS de suivi dès l'expédition.",
              },
              {
                question: 'Quelle est votre politique de retour ?',
                answer: "Vous disposez de 7 jours après réception pour retourner tout article dans son état d'origine et son emballage. Contactez-nous pour organiser le retour.",
              },
              {
                question: 'Comment suivre ma commande ?',
                answer: "Vous recevrez automatiquement un SMS avec un lien de suivi dès que votre commande est expédiée. Vous pouvez aussi nous contacter directement pour un suivi en temps réel.",
              },
              {
                question: 'Puis-je modifier ou annuler ma commande ?',
                answer: "Vous pouvez modifier ou annuler votre commande dans les 2 heures suivant la validation. Après ce délai, contactez-nous rapidement — nous ferons notre maximum.",
              },
            ],
          },
        },
      ],

      about: [
        {
          id: 'general-about-banner',
          type: 'image-banner',
          visible: true,
          props: {
            image: '/templates/premium-electronics-v1/shop-banner-800.webp',
            title: 'Notre Histoire',
            subtitle: 'Une boutique construite sur la confiance, la qualité et le service.',
            ctaLabel: 'Voir la boutique',
            ctaLink: '/shop',
          },
        },
        {
          id: 'general-about-story',
          type: 'rich-text',
          visible: true,
          props: {
            title: 'Qui Sommes-Nous ?',
            text: "Nous sommes une équipe passionnée qui a créé cette boutique avec une idée simple : rendre le shopping en ligne simple, agréable et fiable en Tunisie.\n\nNous sélectionnons chaque produit personnellement, vérifions la qualité avant expédition, et assurons un service client humain et réactif.\n\nVotre satisfaction n'est pas juste une phrase — c'est ce qui nous fait travailler chaque jour.",
          },
        },
        {
          id: 'general-about-trust',
          type: 'trust-strip',
          visible: true,
          props: {},
        },
      ],

      contact: [
        {
          id: 'general-contact-info',
          type: 'rich-text',
          visible: true,
          props: {
            title: 'Nous Contacter',
            text: "Notre équipe est disponible 7j/7 pour vous aider.\n\nWhatsApp : +216 XX XXX XXX\nEmail : contact@votreboutique.tn\nHoraires : 9h–21h tous les jours\n\nNous répondons généralement en moins d'une heure pendant les heures ouvrables. N'hésitez pas à nous contacter pour toute question avant d'acheter !",
          },
        },
        {
          id: 'general-contact-faq',
          type: 'promo-banner',
          visible: true,
          props: {
            text: 'Vous trouverez peut-être votre réponse directement dans notre FAQ.',
            ctaLabel: 'Voir la FAQ',
            ctaLink: '/faq',
            backgroundColor: '',
            textColor: '',
            showIcon: false,
          },
        },
      ],
    },
  },
}

// ─── FOOD & GROCERY ───────────────────────────────────────────────────────────

const foodTemplate: StoreTemplate = {
  id: 'food',
  name: 'Épicerie & Alimentation',
  description: 'Template optimisé pour épiceries, livraison alimentaire, produits frais et circuits courts.',
  niche: 'Alimentation',
  thumbnail: '/templates/preview-food/fd-cat-fruits.webp',
  layout: {
    theme: {
      id: 'retro-vintage',
      primary: 'hsl(142 71% 35%)',
      secondary: 'hsl(142 40% 96%)',
      backgroundColor: '#f7fff7',
      font: 'Poppins',
      radius: '0.75rem',
      ui: {
        productCard: 'default',
        buttons: 'ghost',
        spacing: 'airy',
        headingStyle: 'editorial',
        font: 'serif',
        density: 'comfortable',
        mode: 'light',
        radius: 'md',
      },
    } as any,
    header: {
      props: {
        storeName: null,
        menuItems: [
          { label: 'Accueil', link: '/' },
          { label: 'Boutique', link: '/shop' },
          { label: 'FAQ', link: '/faq' },
          { label: 'À Propos', link: '/about' },
          { label: 'Contact', link: '/contact' },
        ],
        showSearch: true,
        showCart: true,
        sticky: true,
        variant: 'default',
        promoText: '🌿 Produits frais · Livraison 2-4h',
        promoColor: '#14532d',
      },
    },
    footer: {
      props: {
        storeName: true,
        description: 'Produits frais et de qualité livrés directement chez vous — sélectionnés chaque matin.',
        copyright: `© ${new Date().getFullYear()} Votre Boutique. Tous droits réservés.`,
        columns: [
          {
            title: 'Nos Produits',
            links: [
              { label: 'Fruits & Légumes', link: '/category/fruits-legumes' },
              { label: 'Produits Laitiers', link: '/category/laitiers' },
              { label: 'Épicerie Fine', link: '/category/epicerie' },
              { label: 'Boissons', link: '/category/boissons' },
            ],
          },
          {
            title: 'Informations',
            links: [
              { label: 'Livraison', link: '/policy/shipping' },
              { label: 'Retours', link: '/policy/returns' },
              { label: 'FAQ', link: '/faq' },
              { label: 'À Propos', link: '/about' },
              { label: 'Contact', link: '/contact' },
            ],
          },
        ],
        socialLinks: { instagram: 'https://instagram.com', facebook: 'https://facebook.com' },
        showPaymentIcons: true,
      },
    },
    pages: {

      // ── HOME — hero(split) → categories → browse → urgency → catalog ──────
      home: [
        {
          id: 'food-home-announce',
          type: 'announcement-bar',
          visible: true,
          props: {
            text: '🌿 Produits frais du matin · 🚚 Livraison 2-4h · 💳 Paiement à la livraison',
            backgroundColor: '#14532d',
          },
        },
        // split hero: text left (promise) + product image right
        {
          id: 'food-home-hero',
          type: 'hero',
          visible: true,
          props: {
            headline: 'Fraîcheur livrée à votre porte',
            subtitle: 'Fruits, légumes, épicerie fine et produits laitiers sélectionnés chaque matin — livrés en 2 à 4 heures.',
            ctaLabel: 'Explorer les rayons',
            anchorId: 'rayons',
            backgroundImage: '/templates/preview-food/fd-hero-wide.webp',
            layout: 'split',
            overlayOpacity: 15,
            textColor: '#1a2e1a',
          },
        },
        // today's picks before full aisle browse — distinct from category-first templates
        {
          id: 'food-home-featured',
          type: 'featured-collection',
          visible: true,
          props: {
            title: 'Produits du Jour',
            subtitle: "Réceptionnés ce matin — fraîcheur garantie",
            limit: 8,
            columns: 4,
            viewAllLabel: 'Voir toute la boutique',
            viewAllLink: '/shop',
          },
        },
        {
          id: 'food-home-cats',
          type: 'category-grid',
          visible: true,
          props: {},
        },
        {
          id: 'food-home-trust',
          type: 'trust-strip',
          visible: true,
          props: {},
        },
        {
          id: 'food-home-story',
          type: 'image-with-text',
          visible: true,
          props: {
            image: '/templates/preview-food/fd-cat-fruits.webp',
            imageAlt: 'Agriculteurs locaux et marché frais',
            title: 'Directement de nos producteurs locaux',
            text: "Nous travaillons avec des agriculteurs partenaires pour vous apporter les meilleurs produits de chaque saison. Chaque matin, nos équipes sélectionnent les arrivages pour garantir qualité et saveur — sans intermédiaire.",
            ctaLabel: 'Notre démarche',
            ctaLink: '/about',
            layout: 'image-left',
            imageAspect: 'landscape',
          },
        },
        // urgency fires AFTER discovery — not before
        {
          id: 'food-home-promo',
          type: 'promo-banner',
          visible: true,
          props: {
            text: '🎁 Première commande : -15% avec le code BIENVENUE',
            highlight: 'Offre limitée aux nouveaux clients.',
            backgroundColor: '#166534',
            textColor: '#ffffff',
            ctaLabel: 'Commander maintenant',
            ctaLink: '/shop',
            showIcon: true,
          },
        },
        // static far-future date avoids the build-time Date.now() bug
        {
          id: 'food-home-countdown',
          type: 'countdown-timer',
          visible: true,
          props: {
            title: '⚡ Livraison gratuite — se termine dans',
            subtitle: 'Profitez de la livraison offerte sur toute la boutique.',
            targetDate: '2027-06-30T23:59:59.000Z',
            expiredText: 'Offre terminée. Revenez demain pour de nouvelles promotions !',
            backgroundColor: '',
          },
        },
        {
          id: 'food-home-grid',
          type: 'product-grid',
          visible: true,
          props: {
            title: 'Toute la Boutique',
            subtitle: 'Épicerie, produits frais, boissons et spécialités locales',
            limit: 12,
            columns: 4,
            anchorId: 'rayons',
          },
        },
        {
          id: 'food-home-newsletter',
          type: 'newsletter',
          visible: true,
          props: {
            title: 'Recevez nos offres en avant-première',
            subtitle: 'Promotions flash, arrivages du jour et recettes de saison.',
            placeholder: 'Votre email...',
            buttonText: "Je m'inscris",
            backgroundColor: '#f0fdf4',
          },
        },
      ],

      // ── SHOP — strip → catalog. No friction. Pure browse mode. ────────────
      shop: [
        {
          id: 'food-shop-announce',
          type: 'announcement-bar',
          visible: true,
          props: {
            text: '🌿 Produits frais réceptionnés ce matin · 🚚 Livraison 2-4h',
            backgroundColor: '#14532d',
          },
        },
        {
          id: 'food-shop-cats',
          type: 'category-strip',
          visible: true,
          props: {},
        },
        {
          id: 'food-shop-grid',
          type: 'product-grid',
          visible: true,
          props: {
            title: 'Tous nos Produits',
            subtitle: 'Filtrez par catégorie ou recherchez ce dont vous avez besoin',
            limit: 24,
            columns: 4,
            anchorId: 'produits',
          },
        },
      ],

      // ── PRODUCT — freshness highlights + same-day delivery + upsell ────────
      product: [
        {
          id: 'food-pdp-details',
          type: 'product-details',
          visible: true,
          props: {
            highlights: [
              'Réceptionné ce matin — fraîcheur garantie',
              'Sélectionné et contrôlé par notre équipe qualité',
              'Origine locale certifiée — producteurs partenaires',
            ],
            trustShipping: "Livraison en 2 à 4h dans votre zone. Commandes avant 16h livrées le jour même.",
            trustReturns: "Produit non conforme ? Contactez-nous dans l'heure — remplacement ou remboursement immédiat.",
            trustPayment: 'Paiement à la livraison, D17 et Flouci acceptés.',
          },
        },
        {
          id: 'food-pdp-related',
          type: 'related-products',
          visible: true,
          props: {
            title: 'Souvent achetés ensemble',
            limit: 4,
            columns: 4,
          },
        },
      ],

      // ── CATEGORY ─────────────────────────────────────────────────────────
      category: [
        {
          id: 'food-cat-announce',
          type: 'announcement-bar',
          visible: true,
          props: {
            text: '🌿 Produits frais · 🚚 Livraison 2-4h · 💳 Paiement à la livraison',
            backgroundColor: '#14532d',
          },
        },
        {
          id: 'food-cat-strip',
          type: 'category-strip',
          visible: true,
          props: {},
        },
        {
          id: 'food-cat-grid',
          type: 'category-grid',
          visible: true,
          props: {},
        },
      ],

      // ── CART — free shipping nudge + cart + upsell ────────────────────────
      cart: [
        {
          id: 'food-cart-announce',
          type: 'announcement-bar',
          visible: true,
          props: {
            text: '🚚 Livraison gratuite dès 80 TND · Paiement à la livraison disponible',
            backgroundColor: '#14532d',
          },
        },
        {
          id: 'food-cart-lines',
          type: 'cart-lines',
          visible: true,
          props: {},
        },
        {
          id: 'food-cart-summary',
          type: 'cart-summary',
          visible: true,
          props: {},
        },
        {
          id: 'food-cart-upsell',
          type: 'featured-collection',
          visible: true,
          props: {
            title: 'Avez-vous pensé à…',
            subtitle: 'Des essentiels souvent ajoutés avec votre commande',
            limit: 4,
            columns: 4,
            viewAllLabel: '',
            viewAllLink: '',
          },
        },
      ],

      // ── CHECKOUT ─────────────────────────────────────────────────────────
      checkout: [
        {
          id: 'food-checkout-announce',
          type: 'announcement-bar',
          visible: true,
          props: {
            text: '🔒 Commande sécurisée · Livraison 2-4h · Paiement à la livraison disponible',
            backgroundColor: '#14532d',
          },
        },
        {
          id: 'food-checkout-form',
          type: 'checkout-form',
          visible: true,
          props: {},
        },
      ],

      // ── SEARCH — results + popular fallback ───────────────────────────────
      search: [
        {
          id: 'food-search-announce',
          type: 'announcement-bar',
          visible: true,
          props: {
            text: '🌿 Produits frais · 🚚 Livraison 2-4h',
            backgroundColor: '#14532d',
          },
        },
        {
          id: 'food-search-results',
          type: 'search-results',
          visible: true,
          props: {},
        },
        {
          id: 'food-search-fallback',
          type: 'featured-collection',
          visible: true,
          props: {
            title: 'Nos Produits Populaires',
            subtitle: 'Les incontournables du moment',
            limit: 4,
            columns: 4,
            viewAllLabel: 'Voir tout',
            viewAllLink: '/shop',
          },
        },
      ],

      // ── ACCOUNT ───────────────────────────────────────────────────────────
      account: [
        {
          id: 'food-account-announce',
          type: 'announcement-bar',
          visible: true,
          props: {
            text: '🌿 Bienvenue · Retrouvez vos commandes et vos favoris',
            backgroundColor: '#14532d',
          },
        },
      ],

      // ── ABOUT — producer story, local roots, freshness mission ────────────
      about: [
        {
          id: 'food-about-banner',
          type: 'image-banner',
          visible: true,
          props: {
            image: '/templates/preview-food/fd-hero-wide.webp',
            title: 'Notre Mission : La Fraîcheur Accessible',
            subtitle: 'Producteurs locaux, sélection quotidienne, livraison express — au service de votre table.',
            ctaLabel: 'Voir nos produits',
            ctaLink: '/shop',
            overlayOpacity: 40,
            minHeight: 'lg',
            textAlign: 'center',
          },
        },
        {
          id: 'food-about-text',
          type: 'rich-text',
          visible: true,
          props: {
            title: 'Qui sommes-nous ?',
            text: "Nous avons créé cette épicerie avec une conviction simple : tout le monde mérite des produits frais, locaux et de qualité, sans se déplacer.\n\nChaque matin, notre équipe sélectionne les meilleurs produits auprès de nos agriculteurs et producteurs partenaires. Rien ne reste en stock plus d'un jour — ce que vous recevez, c'est ce qui vient d'arriver.\n\nNous livrons en 2 à 4 heures dans votre zone. Votre satisfaction est notre seule mesure de succès.",
          },
        },
        {
          id: 'food-about-producers',
          type: 'image-with-text',
          visible: true,
          props: {
            image: '/templates/preview-food/fd-cat-laitiers.webp',
            imageAlt: 'Agriculteurs partenaires dans leurs champs',
            title: 'Nos Producteurs Partenaires',
            text: "Nous collaborons avec des fermes familiales tunisiennes qui partagent nos valeurs : qualité, traçabilité et respect de la saisonnalité. Acheter chez nous, c'est soutenir directement l'agriculture locale.",
            ctaLabel: 'Voir la boutique',
            ctaLink: '/shop',
            layout: 'image-right',
            imageAspect: 'landscape',
          },
        },
        {
          id: 'food-about-trust',
          type: 'trust-strip',
          visible: true,
          props: {},
        },
      ],

      // ── CONTACT — fast, practical, direct ─────────────────────────────────
      contact: [
        {
          id: 'food-contact-text',
          type: 'rich-text',
          visible: true,
          props: {
            title: 'Contactez-Nous',
            text: "Notre équipe est disponible 7j/7 pour vous aider.\n\nWhatsApp : +216 XX XXX XXX\nEmail : contact@votreboutique.tn\nHoraires : 8h – 22h tous les jours\n\nPour un problème de livraison ou de qualité produit, envoyez-nous un message WhatsApp avec votre numéro de commande. Nous répondons en moins de 30 minutes.",
          },
        },
        {
          id: 'food-contact-faq',
          type: 'promo-banner',
          visible: true,
          props: {
            text: "La plupart des réponses se trouvent dans notre FAQ — consultez-la avant d'écrire.",
            ctaLabel: 'Voir la FAQ',
            ctaLink: '/faq',
            backgroundColor: '',
            textColor: '',
            showIcon: false,
          },
        },
      ],

      // ── FAQ — freshness, delivery, COD, refund ────────────────────────────
      faq: [
        {
          id: 'food-faq',
          type: 'faq-accordion',
          visible: true,
          props: {
            title: 'Questions Fréquentes',
            subtitle: 'Toutes les réponses avant de commander',
            items: [
              {
                question: 'Vos produits sont-ils vraiment frais ?',
                answer: "Oui. Nous réceptionnons nos produits chaque matin de nos agriculteurs partenaires. Rien ne reste en stock plus d'un jour. Ce que vous recevez a été sélectionné le matin même.",
              },
              {
                question: 'Quels sont les délais de livraison ?',
                answer: 'Nous livrons en 2 à 4 heures pour les commandes passées avant 18h. Les commandes après 18h sont livrées le lendemain matin dès 8h.',
              },
              {
                question: 'Puis-je payer à la livraison ?',
                answer: 'Oui, le paiement en espèces à la livraison est disponible sur toutes les commandes. D17, Flouci et Konnect sont également acceptés.',
              },
              {
                question: 'Que faire si un produit ne me satisfait pas ?',
                answer: "Contactez-nous dans l'heure suivant la livraison via WhatsApp avec une photo. Nous organisons immédiatement un remplacement ou un remboursement complet — sans discussion.",
              },
              {
                question: 'Puis-je modifier ou annuler ma commande ?',
                answer: 'Vous pouvez modifier ou annuler dans les 30 minutes suivant la commande. Après ce délai, la préparation est lancée. Contactez-nous rapidement.',
              },
              {
                question: 'Livrez-vous dans toute la Tunisie ?',
                answer: "Nous livrons dans notre zone de couverture locale. Entrez votre adresse au checkout pour confirmer la disponibilité.",
              },
            ],
          },
        },
      ],

      // ── POLICY — SHIPPING ─────────────────────────────────────────────────
      'policy-shipping': [
        {
          id: 'food-policy-shipping',
          type: 'rich-text',
          visible: true,
          props: {
            title: 'Politique de Livraison',
            text: "Délais\nNous livrons en 2 à 4 heures pour les commandes passées avant 18h. Commandes après 18h : livraison le lendemain matin dès 8h.\n\nZone de livraison\nNous livrons dans notre zone de couverture locale. L'adresse est vérifiée au checkout.\n\nFrais de livraison\nLivraison gratuite dès 80 TND d'achat. En dessous de ce montant, des frais fixes s'appliquent selon la zone.\n\nSuivi\nVous recevrez un SMS avec le suivi de votre commande dès l'expédition.",
          },
        },
      ],

      // ── POLICY — RETURNS ──────────────────────────────────────────────────
      'policy-returns': [
        {
          id: 'food-policy-returns',
          type: 'rich-text',
          visible: true,
          props: {
            title: 'Politique de Retours & Remboursements',
            text: "Notre engagement\nSi un produit est abîmé, non frais, ou différent de la description, nous le remplaçons ou vous remboursons — sans condition.\n\nComment procéder\nContactez-nous dans l'heure suivant la livraison via WhatsApp avec votre numéro de commande et une photo du produit concerné.\n\nRemboursements\nLes remboursements sont effectués dans les 24 heures sur votre moyen de paiement original.\n\nProduits non retournables\nPour des raisons d'hygiène alimentaire, les produits frais ouverts ou consommés en partie ne peuvent pas être retournés.",
          },
        },
      ],
    },
  },
}

// ─── KIDS & TOYS ──────────────────────────────────────────────────────────────

const kidsTemplate: StoreTemplate = {
  id: 'kids',
  name: 'Jouets & Enfants',
  description: 'Template coloré et joyeux pour boutiques de jouets, vêtements enfant et puériculture.',
  niche: 'Enfants',
  thumbnail: '/templates/preview-kids/kid-cat-3-6.webp',
  layout: {
    theme: {
      id: 'sport-energy',
      primary: 'hsl(217 91% 55%)',
      secondary: 'hsl(45 93% 95%)',
      backgroundColor: '#fffef0',
      font: 'Poppins',
      radius: '1rem',
      ui: {
        productCard: 'bold',
        buttons: 'pill',
        spacing: 'compact',
        headingStyle: 'bold',
        font: 'sans',
        density: 'tight',
        mode: 'light',
        radius: 'xl',
      },
    } as any,
    header: {
      props: {
        storeName: null,
        menuItems: [
          { label: 'Accueil', link: '/' },
          { label: 'Boutique', link: '/shop' },
          { label: 'Par Âge', link: '/category/3-6-ans' },
          { label: 'FAQ', link: '/faq' },
          { label: 'Contact', link: '/contact' },
        ],
        showSearch: true,
        showCart: true,
        sticky: true,
        variant: 'default',
        promoText: '🔒 Jouets certifiés CE · 🎁 Emballage cadeau offert',
        promoColor: '#1d4ed8',
      },
    },
    footer: {
      props: {
        storeName: true,
        description: 'Des jouets éducatifs et sécurisés pour des enfants épanouis — certifiés CE, testés par des parents.',
        copyright: `© ${new Date().getFullYear()} Votre Boutique. Tous droits réservés.`,
        columns: [
          {
            title: 'Par Âge',
            links: [
              { label: '0-3 ans', link: '/category/0-3-ans' },
              { label: '3-6 ans', link: '/category/3-6-ans' },
              { label: '6-12 ans', link: '/category/6-12-ans' },
              { label: 'Jeux éducatifs', link: '/category/educatifs' },
            ],
          },
          {
            title: 'Aide',
            links: [
              { label: 'Livraison', link: '/policy/shipping' },
              { label: 'Retours', link: '/policy/returns' },
              { label: 'FAQ', link: '/faq' },
              { label: 'À Propos', link: '/about' },
              { label: 'Contact', link: '/contact' },
            ],
          },
        ],
        socialLinks: { instagram: 'https://instagram.com', facebook: 'https://facebook.com', tiktok: 'https://tiktok.com' },
        showPaymentIcons: true,
      },
    },
    pages: {

      // ── HOME — banner → age-groups → safety → browse → parent-reviews → catalog
      home: [
        {
          id: 'kids-home-announce',
          type: 'announcement-bar',
          visible: true,
          props: {
            text: '🎉 Livraison gratuite dès 80 TND · 🔒 Jouets certifiés CE · 🎁 Emballage cadeau offert',
            backgroundColor: '#1d4ed8',
          },
        },
        // age-first navigation — distinct from banner-led lifestyle templates
        {
          id: 'kids-home-cats',
          type: 'category-grid',
          visible: true,
          props: {},
        },
        // high-energy playful banner — low overlay, image does the work
        {
          id: 'kids-home-banner',
          type: 'image-banner',
          visible: true,
          props: {
            image: '/templates/preview-kids/kid-hero-wide.webp',
            title: 'La magie du jeu, livrée chez vous',
            subtitle: "Des jouets éducatifs, sécurisés et irrésistibles pour éveiller la curiosité de vos enfants.",
            ctaLabel: 'Découvrir par âge',
            ctaLink: '/shop',
            overlayOpacity: 30,
            minHeight: 'xl',
            textAlign: 'left',
          },
        },
        // safety promo BEFORE any product — parents need this before they browse
        {
          id: 'kids-home-safety',
          type: 'promo-banner',
          visible: true,
          props: {
            text: '🔒 Tous nos jouets sont certifiés CE et conformes aux normes européennes de sécurité pour enfants',
            highlight: 'Testés. Approuvés. Sécurisés.',
            backgroundColor: '#eff6ff',
            textColor: '#1e40af',
            ctaLabel: 'Nos engagements',
            ctaLink: '/faq',
            showIcon: false,
          },
        },
        {
          id: 'kids-home-featured',
          type: 'featured-collection',
          visible: true,
          props: {
            title: '⭐ Les Favoris des Enfants',
            subtitle: 'Les jouets les plus commandés par les familles tunisiennes',
            limit: 8,
            columns: 4,
            viewAllLabel: 'Voir toute la sélection',
            viewAllLink: '/shop',
          },
        },
        // parent testimonials BEFORE full grid — highest conversion signal for kids
        {
          id: 'kids-home-testimonials',
          type: 'testimonials',
          visible: true,
          props: {
            title: 'Ce que disent les parents',
            subtitle: 'Avis vérifiés de vraies familles',
            items: [
              {
                name: 'Imen T.',
                role: 'Maman de 2 enfants',
                text: "Mon fils de 5 ans ne veut plus s'en séparer. La qualité est bien au-dessus de ce que j'espérais, et la livraison était là le lendemain. Je recommande sans hésiter.",
                rating: 5,
              },
              {
                name: 'Karim B.',
                role: 'Papa — enfant de 4 ans',
                text: "Ce puzzle éducatif est exactement ce que je cherchais. Mon fils apprend les couleurs et les formes en jouant. Certifié CE, matériaux solides — qualité vraiment sérieuse.",
                rating: 5,
              },
              {
                name: 'Sonia M.',
                role: 'Acheteuse vérifiée',
                text: "L'emballage cadeau était parfait pour l'anniversaire de ma fille. Elle était ravie de déballer. Le jouet tient bien ses promesses — aucune pièce dangereuse.",
                rating: 5,
              },
            ],
          },
        },
        {
          id: 'kids-home-story',
          type: 'image-with-text',
          visible: true,
          props: {
            image: '/templates/preview-kids/kid-prod-livre-tissu.webp',
            imageAlt: 'Livre tissu éveil et jeu doux pour bébé',
            title: "Jouer, c'est apprendre",
            text: "Chaque jouet dans notre sélection a été choisi pour son potentiel de développement. Motricité fine, langage, logique, créativité — chaque aire de jeu correspond à une étape clé du développement de l'enfant.",
            ctaLabel: 'Notre philosophie',
            ctaLink: '/about',
            layout: 'image-right',
            imageAspect: 'square',
          },
        },
        {
          id: 'kids-home-gift-promo',
          type: 'promo-banner',
          visible: true,
          props: {
            text: '🎁 Emballage cadeau offert sur toutes les commandes — parfait pour un anniversaire',
            highlight: '',
            backgroundColor: '',
            textColor: '',
            ctaLabel: 'Commander un cadeau',
            ctaLink: '/shop',
            showIcon: true,
          },
        },
        {
          id: 'kids-home-grid',
          type: 'product-grid',
          visible: true,
          props: {
            title: 'Tous nos Jouets',
            subtitle: 'Éducatif, créatif, sportif — pour tous les âges de 0 à 12 ans',
            limit: 12,
            columns: 4,
            anchorId: 'jouets',
          },
        },
        {
          id: 'kids-home-newsletter',
          type: 'newsletter',
          visible: true,
          props: {
            title: '🎈 Idées Cadeaux & Nouveautés',
            subtitle: "Recevez nos suggestions de cadeaux selon l'âge et nos nouvelles arrivées.",
            placeholder: 'Votre email...',
            buttonText: "Je m'inscris",
            backgroundColor: '#fffbeb',
          },
        },
      ],

      // ── SHOP — age-group strip → popular → full catalog ───────────────────
      shop: [
        {
          id: 'kids-shop-announce',
          type: 'announcement-bar',
          visible: true,
          props: {
            text: '🎁 Emballage cadeau offert · 🔒 Jouets certifiés CE · Livraison 24-48h',
            backgroundColor: '#1d4ed8',
          },
        },
        {
          id: 'kids-shop-cats',
          type: 'category-strip',
          visible: true,
          props: {},
        },
        {
          id: 'kids-shop-featured',
          type: 'featured-collection',
          visible: true,
          props: {
            title: 'Les Plus Populaires',
            subtitle: 'Ce que les enfants adorent en ce moment',
            limit: 4,
            columns: 4,
            viewAllLabel: '',
            viewAllLink: '',
          },
        },
        {
          id: 'kids-shop-grid',
          type: 'product-grid',
          visible: true,
          props: {
            title: 'Tous les Jouets',
            subtitle: 'Filtrez par âge, catégorie ou prix',
            limit: 24,
            columns: 4,
            anchorId: 'produits',
          },
        },
      ],

      // ── PRODUCT — safety + age + gift wrap tabs ───────────────────────────
      product: [
        {
          id: 'kids-pdp-details',
          type: 'product-details',
          visible: true,
          props: {
            highlights: [
              'Certifié CE — conforme aux normes européennes de sécurité enfant',
              'Sans BPA, sans métaux lourds, matériaux non toxiques',
              'Emballage cadeau offert sur demande',
            ],
            trustShipping: "Livraison soignée sous 24-48h partout en Tunisie.",
            trustReturns: "Retours acceptés 7 jours — article dans son emballage d'origine.",
            trustPayment: 'Paiement à la livraison, D17 et Konnect acceptés.',
          },
        },
        {
          id: 'kids-pdp-tabs',
          type: 'product-tabs',
          visible: true,
          props: {
            tabs: [
              {
                label: 'Description',
                content: "Découvrez les caractéristiques complètes de ce jouet — stimulation, fonctionnalités et contenu de la boîte.",
              },
              {
                label: 'Âge recommandé',
                content: "L'âge recommandé figure sur la fiche produit et sur l'emballage. En cas de doute entre deux tranches d'âge, contactez-nous pour un conseil personnalisé.",
              },
              {
                label: 'Sécurité & Certifications',
                content: "Ce jouet est certifié CE et répond aux normes européennes EN71. Il ne contient pas de BPA, de phtalates ni de métaux lourds. Tous les matériaux utilisés sont testés et approuvés pour les enfants.",
              },
              {
                label: 'Livraison & Cadeaux',
                content: "Livraison sous 24-48h avec emballage soigné. Sur demande dans les commentaires de commande, nous ajoutons l'emballage cadeau et un message personnalisé — gratuit.",
              },
            ],
          },
        },
        {
          id: 'kids-pdp-related',
          type: 'related-products',
          visible: true,
          props: {
            title: "D'autres jouets pour cet âge",
            limit: 4,
            columns: 4,
          },
        },
      ],

      // ── CATEGORY ─────────────────────────────────────────────────────────
      category: [
        {
          id: 'kids-cat-announce',
          type: 'announcement-bar',
          visible: true,
          props: {
            text: '🔒 Jouets certifiés CE · 🎁 Emballage cadeau offert · Livraison 24-48h',
            backgroundColor: '#1d4ed8',
          },
        },
        {
          id: 'kids-cat-safety',
          type: 'promo-banner',
          visible: true,
          props: {
            text: '🔒 Tous les jouets de cette catégorie sont certifiés CE et conformes aux normes européennes',
            highlight: '',
            backgroundColor: '#eff6ff',
            textColor: '#1e40af',
            ctaLabel: '',
            ctaLink: '',
            showIcon: false,
          },
        },
        {
          id: 'kids-cat-strip',
          type: 'category-strip',
          visible: true,
          props: {},
        },
        {
          id: 'kids-cat-grid',
          type: 'category-grid',
          visible: true,
          props: {},
        },
      ],

      // ── CART — gift wrap reminder + cart + accessories upsell ─────────────
      cart: [
        {
          id: 'kids-cart-announce',
          type: 'announcement-bar',
          visible: true,
          props: {
            text: '🎁 Emballage cadeau offert · 🚚 Livraison 24-48h partout en Tunisie',
            backgroundColor: '#1d4ed8',
          },
        },
        {
          id: 'kids-cart-lines',
          type: 'cart-lines',
          visible: true,
          props: {},
        },
        {
          id: 'kids-cart-summary',
          type: 'cart-summary',
          visible: true,
          props: {},
        },
        {
          id: 'kids-cart-upsell',
          type: 'featured-collection',
          visible: true,
          props: {
            title: 'Souvent offerts ensemble',
            subtitle: 'Des jouets complémentaires pour compléter le cadeau',
            limit: 4,
            columns: 4,
            viewAllLabel: '',
            viewAllLink: '',
          },
        },
      ],

      // ── CHECKOUT ─────────────────────────────────────────────────────────
      checkout: [
        {
          id: 'kids-checkout-announce',
          type: 'announcement-bar',
          visible: true,
          props: {
            text: '🎁 Emballage cadeau offert · 🔒 Commande sécurisée · Paiement à la livraison disponible',
            backgroundColor: '#1d4ed8',
          },
        },
        {
          id: 'kids-checkout-form',
          type: 'checkout-form',
          visible: true,
          props: {},
        },
      ],

      // ── SEARCH — results + suggestions ───────────────────────────────────
      search: [
        {
          id: 'kids-search-announce',
          type: 'announcement-bar',
          visible: true,
          props: {
            text: '🔒 Jouets certifiés CE · 🎁 Emballage cadeau offert',
            backgroundColor: '#1d4ed8',
          },
        },
        {
          id: 'kids-search-results',
          type: 'search-results',
          visible: true,
          props: {},
        },
        {
          id: 'kids-search-fallback',
          type: 'featured-collection',
          visible: true,
          props: {
            title: 'Les Plus Populaires',
            subtitle: 'Les jouets que les enfants adorent en ce moment',
            limit: 4,
            columns: 4,
            viewAllLabel: 'Voir tout',
            viewAllLink: '/shop',
          },
        },
      ],

      // ── ACCOUNT ───────────────────────────────────────────────────────────
      account: [
        {
          id: 'kids-account-announce',
          type: 'announcement-bar',
          visible: true,
          props: {
            text: '🎈 Bienvenue · Retrouvez vos commandes et vos jouets favoris',
            backgroundColor: '#1d4ed8',
          },
        },
      ],

      // ── ABOUT — educational mission, safety story ─────────────────────────
      about: [
        {
          id: 'kids-about-banner',
          type: 'image-banner',
          visible: true,
          props: {
            image: '/templates/preview-kids/kid-hero-wide.webp',
            title: 'Des Jouets Pensés par des Parents',
            subtitle: 'Sécurité, éducation et plaisir — notre sélection est pensée pour le développement de votre enfant.',
            ctaLabel: 'Découvrir nos jouets',
            ctaLink: '/shop',
            overlayOpacity: 35,
            minHeight: 'lg',
            textAlign: 'center',
          },
        },
        {
          id: 'kids-about-text',
          type: 'rich-text',
          visible: true,
          props: {
            title: 'Notre Mission',
            text: "Nous croyons que chaque enfant mérite des jouets qui stimulent sa créativité et son développement.\n\nNotre sélection est pensée par des parents pour des parents. Chaque produit est choisi selon trois critères : sécurité (certification CE obligatoire), valeur éducative (motricité, langage, logique), et plaisir (parce que jouer doit rester amusant).\n\nNous offrons l'emballage cadeau sur toutes les commandes parce que chaque jouet peut être un moment de bonheur pour un enfant.",
          },
        },
        {
          id: 'kids-about-story',
          type: 'image-with-text',
          visible: true,
          props: {
            image: '/templates/preview-kids/kid-prod-circuit.webp',
            imageAlt: 'Enfants jouant avec des jouets éducatifs colorés',
            title: 'Certifiés. Testés. Approuvés.',
            text: "Tous nos jouets portent la certification CE et respectent les normes européennes EN71. Aucun produit sans BPA, sans phtalates, sans métaux lourds. Votre enfant mérite le meilleur — nous le garantissons.",
            ctaLabel: 'Voir notre FAQ sécurité',
            ctaLink: '/faq',
            layout: 'image-left',
            imageAspect: 'square',
          },
        },
        {
          id: 'kids-about-trust',
          type: 'trust-strip',
          visible: true,
          props: {},
        },
      ],

      // ── CONTACT ───────────────────────────────────────────────────────────
      contact: [
        {
          id: 'kids-contact-text',
          type: 'rich-text',
          visible: true,
          props: {
            title: 'Contactez-nous',
            text: "Notre équipe de parents est disponible pour vous conseiller.\n\nWhatsApp : +216 XX XXX XXX\nEmail : contact@votreboutique.tn\nHoraires : 9h – 21h, 7 jours sur 7\n\nPour un conseil sur le choix d'un jouet adapté à l'âge de votre enfant, envoyez-nous un message — nous vous répondons en moins d'une heure.",
          },
        },
        {
          id: 'kids-contact-faq',
          type: 'promo-banner',
          visible: true,
          props: {
            text: 'Consultez notre FAQ pour les questions sur les certifications, la livraison et les retours.',
            ctaLabel: 'Voir la FAQ',
            ctaLink: '/faq',
            backgroundColor: '',
            textColor: '',
            showIcon: false,
          },
        },
      ],

      // ── FAQ — safety, certifications, age ranges, returns, gift wrap ──────
      faq: [
        {
          id: 'kids-faq',
          type: 'faq-accordion',
          visible: true,
          props: {
            title: 'Questions Fréquentes',
            subtitle: 'Tout ce que les parents veulent savoir',
            items: [
              {
                question: 'Les jouets sont-ils certifiés et sécurisés ?',
                answer: "Oui, tous nos jouets portent la certification CE et respectent les normes européennes EN71. Ils sont exempts de BPA, de phtalates et de métaux lourds. La certification figure sur chaque fiche produit.",
              },
              {
                question: 'Comment choisir le bon jouet selon l\'âge ?',
                answer: "Chaque fiche produit indique la tranche d'âge recommandée. Pour un conseil personnalisé, contactez-nous via WhatsApp avec l'âge de votre enfant et ses centres d'intérêt.",
              },
              {
                question: "Proposez-vous un emballage cadeau ?",
                answer: "Oui, l'emballage cadeau est offert sur toutes les commandes. Mentionnez-le dans les commentaires de votre commande, et ajoutez un message personnalisé si vous le souhaitez.",
              },
              {
                question: "Quels sont les délais de livraison ?",
                answer: "Livraison sous 24-48h ouvrables partout en Tunisie. Vous recevrez un SMS de confirmation dès l'expédition.",
              },
              {
                question: "Puis-je retourner un jouet ?",
                answer: "Oui, les retours sont acceptés sous 7 jours pour tout article dans son emballage d'origine. Pour les cadeaux, contactez-nous — nous trouvons toujours une solution.",
              },
              {
                question: "Le jouet correspond-il exactement aux photos ?",
                answer: "Oui, nous ne vendons que des produits identiques aux photos. Si votre commande arrive différente de la description, nous le remplaçons immédiatement.",
              },
            ],
          },
        },
      ],

      // ── POLICY — SHIPPING ─────────────────────────────────────────────────
      'policy-shipping': [
        {
          id: 'kids-policy-shipping',
          type: 'rich-text',
          visible: true,
          props: {
            title: 'Politique de Livraison',
            text: "Délais\nLivraison sous 24-48h ouvrables partout en Tunisie.\n\nEmballage\nTous les jouets sont emballés avec soin pour protéger le produit pendant le transport. L'emballage cadeau est offert sur demande.\n\nFrais de livraison\nLivraison gratuite dès 80 TND d'achat.\n\nSuivi\nVous recevrez un SMS avec un lien de suivi dès l'expédition de votre commande.",
          },
        },
      ],

      // ── POLICY — RETURNS ──────────────────────────────────────────────────
      'policy-returns': [
        {
          id: 'kids-policy-returns',
          type: 'rich-text',
          visible: true,
          props: {
            title: 'Politique de Retours',
            text: "Retours\nLes retours sont acceptés sous 7 jours pour tout article dans son emballage d'origine, non ouvert.\n\nComment procéder\nContactez-nous via WhatsApp ou email avec votre numéro de commande. Nous vous indiquons la procédure de retour.\n\nRemboursements\nLe remboursement est effectué dans les 48 heures après réception du retour.\n\nCas particuliers\nPour les cadeaux ouverts par l'enfant, contactez-nous — nous évaluons chaque situation et trouvons la meilleure solution.",
          },
        },
      ],
    },
  },
}

// ─── HOME DECOR ───────────────────────────────────────────────────────────────

const homeDecorTemplate: StoreTemplate = {
  id: 'home-decor',
  name: 'Maison & Décoration',
  description: 'Template lifestyle pour décoration intérieure, mobilier, bougies, art et objets de maison.',
  niche: 'Décoration',
  thumbnail: '/templates/home-decor-pinterest-v1/hero-800.webp',
  layout: {
    theme: {
      id: 'minimal-white',
      primary: 'hsl(25 70% 40%)',
      secondary: 'hsl(25 30% 96%)',
      backgroundColor: '#fdfcfa',
      font: 'Lora',
      radius: '0.5rem',
      ui: {
        productCard: 'minimal',
        buttons: 'sharp',
        spacing: 'airy',
        headingStyle: 'editorial',
        font: 'serif',
        density: 'comfortable',
        mode: 'light',
        radius: 'none',
      },
    } as any,
    header: {
      props: {
        storeName: null,
        menuItems: [
          { label: 'Accueil', link: '/' },
          { label: 'Collections', link: '/shop' },
          { label: 'Nouveautés', link: '/shop' },
          { label: 'À Propos', link: '/about' },
          { label: 'Contact', link: '/contact' },
        ],
        showSearch: true,
        showCart: true,
        sticky: true,
        variant: 'default',
      },
    },
    footer: {
      props: {
        storeName: true,
        description: 'Transformez votre intérieur avec des pièces uniques soigneusement sélectionnées.',
        copyright: `© ${new Date().getFullYear()} Votre Boutique. Tous droits réservés.`,
        columns: [
          {
            title: 'Collections',
            links: [
              { label: 'Salon', link: '/category/salon' },
              { label: 'Chambre', link: '/category/chambre' },
              { label: 'Cuisine', link: '/category/cuisine' },
              { label: 'Bougies & Senteurs', link: '/category/bougies' },
            ],
          },
          {
            title: 'Service',
            links: [
              { label: 'Livraison', link: '/policy/shipping' },
              { label: 'Retours', link: '/policy/returns' },
              { label: 'FAQ', link: '/faq' },
              { label: 'Contact', link: '/contact' },
            ],
          },
        ],
        socialLinks: { instagram: 'https://instagram.com', facebook: 'https://facebook.com' },
        showPaymentIcons: true,
      },
    },
    pages: {
      home: [
        // ── 1. Announce bar ──────────────────────────────────────────────────
        {
          id: 'decor-announce',
          type: 'announcement-bar',
          visible: true,
          props: { text: '🏠 Livraison soignée · Retours 14 jours · Emballage cadeau disponible', backgroundColor: '#5c3d1e' },
        },
        // ── 2. Full-width lifestyle banner ───────────────────────────────────
        {
          id: 'decor-banner',
          type: 'image-banner',
          visible: true,
          props: {
            image: '/templates/home-decor-pinterest-v1/hero-1200.webp',
            title: "L'Art de Vivre à la Maison",
            subtitle: 'Des pièces de décoration uniques qui racontent une histoire et transforment chaque espace.',
            ctaLabel: 'Découvrir les collections',
            ctaLink: '/shop',
            overlayOpacity: 30,
            minHeight: 'xl',
          },
        },
        // ── 3. Category grid (browse by room) — before long-form stories
        {
          id: 'decor-cats',
          type: 'category-grid',
          visible: true,
          props: {},
        },
        // ── 4. Lifestyle storytelling — magazine pacing after wayfinding
        {
          id: 'decor-story1',
          type: 'image-with-text',
          visible: true,
          props: {
            image: '/templates/home-decor-pinterest-v1/home-052-1200.webp',
            imageAlt: 'Intérieur décoré avec goût et équilibre',
            title: 'Design & Artisanat',
            text: "Chaque pièce de notre collection est choisie pour son esthétique, sa qualité et son histoire. Nous collaborons avec des artisans tunisiens et internationaux pour vous apporter des objets uniques.",
            ctaLabel: 'Notre histoire',
            ctaLink: '/about',
            layout: 'image-left',
            imageAspect: 'portrait',
          },
        },
        // ── 5. Signature pieces (editorial curation) ─────────────────────────
        // 3 columns creates premium feel — more breathing room for decor items
        {
          id: 'decor-featured',
          type: 'featured-collection',
          visible: true,
          props: {
            title: 'Pièces Signature',
            subtitle: 'Une sélection soignée pour sublimer votre intérieur',
            limit: 6,
            columns: 3,
            viewAllLabel: 'Voir tout',
            viewAllLink: '/shop',
          },
        },
        // ── 6. Craftsmanship / values storytelling ───────────────────────────
        // Second story block creates magazine-editorial feel unique to decor
        {
          id: 'decor-story2',
          type: 'image-with-text',
          visible: true,
          props: {
            image: '/templates/home-decor-pinterest-v1/carre-016-800.webp',
            imageAlt: 'Atelier artisanal, emballage soigné',
            title: 'Livraison avec Soin',
            text: "Chaque objet est emballé avec le plus grand soin pour arriver chez vous en parfait état. Nous utilisons des emballages éco-responsables pour protéger votre achat et la planète.",
            ctaLabel: 'Nos engagements',
            ctaLink: '/about',
            layout: 'image-right',
            imageAspect: 'square',
          },
        },
        // ── 7. Seasonal promo banner ─────────────────────────────────────────
        // Promo over countdown — decor buyers are not impulse buyers
        {
          id: 'decor-promo',
          type: 'promo-banner',
          visible: true,
          props: {
            text: '✨ Nouvelle Collection — Découvrez les nouvelles arrivées',
            ctaLabel: 'Voir la collection',
            ctaLink: '/shop',
            backgroundColor: '#78350f',
            textColor: '#fef3c7',
            showIcon: false,
          },
        },
        // ── 8. Full catalog grid — 3 columns for spacious, premium layout ────
        {
          id: 'decor-grid',
          type: 'product-grid',
          visible: true,
          props: { title: 'Toute la Collection', subtitle: 'Salon, chambre, cuisine — des pièces pour chaque espace', limit: 12, columns: 3, anchorId: 'collection' },
        },
        // ── 9. Testimonials ──────────────────────────────────────────────────
        {
          id: 'decor-testimonials',
          type: 'testimonials',
          visible: true,
          props: {
            title: 'Ils ont transformé leur intérieur',
            subtitle: 'Avis vérifiés de nos clients',
            items: [
              { name: 'Nadia A.', role: 'Décoratrice', text: "Des pièces de qualité exceptionnelle, exactement comme sur les photos. Mon salon a été complètement transformé.", rating: 5 },
              { name: 'Riadh M.', role: 'Client fidèle', text: "Troisième commande et toujours aussi satisfait. L'emballage est soigné et la livraison rapide.", rating: 5 },
              { name: 'Hela B.', role: 'Acheteuse vérifiée', text: "J'ai trouvé exactement ce que je cherchais pour ma chambre. Prix juste, qualité au rendez-vous.", rating: 5 },
            ],
          },
        },
        // ── 10. Newsletter ───────────────────────────────────────────────────
        {
          id: 'decor-newsletter',
          type: 'newsletter',
          visible: true,
          props: { title: 'Inspiration & Nouveautés', subtitle: 'Recevez nos nouvelles collections et idées déco en avant-première.', placeholder: 'Votre email...', buttonText: "S'inscrire" },
        },
      ],
      shop: [
        { id: 'decor-shop-cats', type: 'category-strip', visible: true, props: {} },
        { id: 'decor-shop-grid', type: 'product-grid', visible: true, props: { title: 'Toutes les Collections', limit: 24, columns: 4 } },
      ],
      product: [
        { id: 'decor-product', type: 'product-details', visible: true, props: {} },
        { id: 'decor-related', type: 'related-products', visible: true, props: { title: 'Vous aimerez aussi', limit: 4 } },
      ],
      category: [
        {
          id: 'decor-cat-hero',
          type: 'image-banner',
          visible: true,
          props: {
            image: '/templates/home-decor-pinterest-v1/home-086-800.webp',
            title: 'Ambiances par pièce',
            subtitle: 'Salon, chambre, cuisine — une lecture magazine pour cette catégorie.',
            ctaLabel: 'Toute la boutique',
            ctaLink: '/shop',
            overlayOpacity: 40,
            minHeight: 'lg',
            textAlign: 'center',
          },
        },
        { id: 'decor-cat-strip', type: 'category-strip', visible: true, props: {} },
        { id: 'decor-cat-grid', type: 'category-grid', visible: true, props: {} },
      ],
      cart: [
        { id: 'decor-cart-lines', type: 'cart-lines', visible: true, props: {} },
        { id: 'decor-cart-summary', type: 'cart-summary', visible: true, props: {} },
      ],
      checkout: [{ id: 'decor-checkout', type: 'checkout-form', visible: true, props: {} }],
      faq: [
        {
          id: 'decor-faq',
          type: 'faq-accordion',
          visible: true,
          props: {
            title: 'Questions Fréquentes',
            items: [
              { question: 'Combien de temps prend la livraison ?', answer: '2-4 jours ouvrables dans toute la Tunisie. Les objets fragiles sont emballés avec soin.' },
              { question: 'Puis-je retourner un article ?', answer: 'Oui, retours acceptés sous 14 jours si le produit est en parfait état.' },
              { question: 'Proposez-vous l\'emballage cadeau ?', answer: 'Oui, précisez-le dans les commentaires de commande, c\'est offert !' },
            ],
          },
        },
      ],
      about: [
        {
          id: 'decor-about',
          type: 'rich-text',
          visible: true,
          props: { title: 'Notre Vision', text: "Nous croyons que chaque maison mérite d'être belle. Notre sélection combine design contemporain, artisanat authentique et accessibilité pour vous permettre de créer un intérieur qui vous ressemble." },
        },
        { id: 'decor-about-trust', type: 'trust-strip', visible: true, props: {} },
      ],
      contact: [
        {
          id: 'decor-contact',
          type: 'rich-text',
          visible: true,
          props: { title: 'Contactez-nous', text: "WhatsApp : +216 XX XXX XXX\nEmail : contact@votreboutique.tn\nDisponible du lundi au samedi de 9h à 20h." },
        },
      ],
    },
  },
}

// ─── PHARMACY / PARAPHARMACIE ─────────────────────────────────────────────────

const pharmacyTemplate: StoreTemplate = {
  id: 'pharmacy',
  name: 'Parapharmacie & Santé',
  description: 'Template professionnel pour parapharmacie, compléments alimentaires, matériel médical et bien-être.',
  niche: 'Santé',
  thumbnail: '/templates/preview-pharmacy/prm-cat-medical.webp',
  layout: {
    theme: {
      id: 'neon-glass',
      primary: 'hsl(199 89% 38%)',
      secondary: 'hsl(199 60% 96%)',
      backgroundColor: '#f5fbff',
      font: 'Inter',
      radius: '0.5rem',
      ui: {
        productCard: 'minimal',
        buttons: 'ghost',
        spacing: 'compact',
        headingStyle: 'clean',
        font: 'sans',
        density: 'comfortable',
        mode: 'light',
        radius: 'xl',
      },
    } as any,
    header: {
      props: {
        storeName: null,
        menuItems: [
          { label: 'Accueil', link: '/' },
          { label: 'Boutique', link: '/shop' },
          { label: 'Par Gamme', link: '/category/corps' },
          { label: 'Conseils', link: '/faq' },
          { label: 'Contact', link: '/contact' },
        ],
        showSearch: true,
        showCart: true,
        sticky: true,
        variant: 'default',
      },
    },
    footer: {
      props: {
        storeName: true,
        description: 'Votre partenaire santé et bien-être, au service de votre qualité de vie.',
        copyright: `© ${new Date().getFullYear()} Votre Boutique. Tous droits réservés.`,
        columns: [
          {
            title: 'Nos Gammes',
            links: [
              { label: 'Soins du corps', link: '/category/corps' },
              { label: 'Compléments', link: '/category/complements' },
              { label: 'Matériel médical', link: '/category/medical' },
              { label: 'Bébé & Maternité', link: '/category/bebe' },
            ],
          },
          {
            title: 'Aide',
            links: [
              { label: 'Conseils santé', link: '/faq' },
              { label: 'Livraison', link: '/policy/shipping' },
              { label: 'Retours', link: '/policy/returns' },
              { label: 'Contact', link: '/contact' },
            ],
          },
        ],
        socialLinks: { instagram: 'https://instagram.com', facebook: 'https://facebook.com' },
        showPaymentIcons: true,
      },
    },
    pages: {
      home: [
        // ── 1. Announce bar ──────────────────────────────────────────────────
        {
          id: 'pharma-announce',
          type: 'announcement-bar',
          visible: true,
          props: { text: '🚚 Livraison rapide · ✅ Produits certifiés · 💬 Conseil pharmacien disponible', backgroundColor: '#0c4a6e' },
        },
        // ── 2. Hero banner ───────────────────────────────────────────────────
        {
          id: 'pharma-hero',
          type: 'image-banner',
          visible: true,
          props: {
            image: '/templates/preview-pharmacy/prm-hero-wide.webp',
            title: 'Votre Santé, Notre Priorité',
            subtitle: "Compléments alimentaires, soins et matériel médical certifiés. Conseils d'experts inclus avec chaque commande.",
            ctaLabel: 'Découvrir nos produits',
            ctaLink: '/shop',
            overlayOpacity: 40,
            minHeight: 'xl',
          },
        },
        // ── 3. Certification promo — proof before generic trust strip
        {
          id: 'pharma-cert-promo',
          type: 'promo-banner',
          visible: true,
          props: {
            text: 'Tous nos produits sont certifiés par les autorités sanitaires et sélectionnés par des professionnels de santé.',
            highlight: 'Qualité garantie.',
            backgroundColor: '',
            textColor: '',
            ctaLabel: 'Nos engagements',
            ctaLink: '/about',
            showIcon: true,
          },
        },
        // ── 4. Trust strip (CRITICAL for health products — rational purchase) ──
        {
          id: 'pharma-trust',
          type: 'trust-strip',
          visible: true,
          props: {},
        },
        // ── 5. Category grid by health range ─────────────────────────────────
        {
          id: 'pharma-cats',
          type: 'category-grid',
          visible: true,
          props: {},
        },
        // ── 6. Most requested products ───────────────────────────────────────
        {
          id: 'pharma-featured',
          type: 'featured-collection',
          visible: true,
          props: {
            title: 'Les Plus Demandés',
            subtitle: 'Nos produits les plus conseillés par notre équipe',
            limit: 6,
            columns: 3,
            viewAllLabel: 'Voir tout',
            viewAllLink: '/shop',
          },
        },
        // ── 7. FAQ — health buyers research before buying ─────────────────────
        // Answering objections before the full grid increases purchase confidence
        {
          id: 'pharma-home-faq',
          type: 'faq-accordion',
          visible: true,
          props: {
            title: 'Vos Questions Santé',
            subtitle: 'Des réponses claires avant de commander',
            items: [
              { question: 'Les produits sont-ils certifiés ?', answer: 'Oui, tous nos produits sont certifiés par les autorités sanitaires compétentes. Nous ne travaillons qu\'avec des marques reconnues.' },
              { question: 'Puis-je avoir un conseil avant d\'acheter ?', answer: 'Absolument ! Contactez-nous par WhatsApp et notre équipe répondra à vos questions.' },
              { question: 'Les compléments alimentaires sont-ils adaptés à tout le monde ?', answer: 'Certains produits peuvent avoir des contre-indications. Consultez toujours votre médecin en cas de doute.' },
              { question: 'Quels sont les délais de livraison ?', answer: 'Livraison en 24-48h dans toute la Tunisie.' },
            ],
          },
        },
        // ── 8. Full catalog grid ─────────────────────────────────────────────
        {
          id: 'pharma-home-grid',
          type: 'product-grid',
          visible: true,
          props: { title: 'Toute la Gamme', subtitle: 'Soins, compléments, bébé et matériel médical', limit: 12, columns: 4, anchorId: 'produits' },
        },
        // ── 9. Newsletter ────────────────────────────────────────────────────
        {
          id: 'pharma-newsletter',
          type: 'newsletter',
          visible: true,
          props: { title: 'Conseils Santé', subtitle: 'Recevez nos conseils et promotions exclusives.', placeholder: 'Votre email...', buttonText: "S'inscrire" },
        },
      ],
      shop: [
        { id: 'pharma-shop-cats', type: 'category-strip', visible: true, props: {} },
        { id: 'pharma-shop-grid', type: 'product-grid', visible: true, props: { title: 'Tous nos Produits', limit: 24, columns: 4 } },
      ],
      product: [
        { id: 'pharma-product', type: 'product-details', visible: true, props: {} },
        { id: 'pharma-product-tabs', type: 'product-tabs', visible: true, props: {} },
        { id: 'pharma-related', type: 'related-products', visible: true, props: { title: 'Complémentaires', limit: 4 } },
      ],
      category: [
        {
          id: 'pharma-cat-announce',
          type: 'announcement-bar',
          visible: true,
          props: {
            text: 'Produits certifiés · Conseil avant achat sur compléments — WhatsApp 7j/7',
            backgroundColor: '#0c4a6e',
          },
        },
        {
          id: 'pharma-cat-trust',
          type: 'trust-strip',
          visible: true,
          props: {},
        },
        {
          id: 'pharma-cat-strip',
          type: 'category-strip',
          visible: true,
          props: {},
        },
        {
          id: 'pharma-cat-faq',
          type: 'faq-accordion',
          visible: true,
          props: {
            title: 'Avant de parcourir cette gamme',
            subtitle: 'Lecture rapide — 30 secondes',
            items: [
              {
                question: 'Les compléments remplacent-ils un avis médical ?',
                answer: 'Non. En cas de traitement ou de grossesse, demandez l’avis de votre professionnel de santé.',
              },
              {
                question: 'Comment vérifier la conformité ?',
                answer: 'Chaque fiche produit indique les mentions réglementaires et le pays d’origine.',
              },
            ],
          },
        },
        { id: 'pharma-cat-grid', type: 'category-grid', visible: true, props: {} },
      ],
      cart: [
        { id: 'pharma-cart-lines', type: 'cart-lines', visible: true, props: {} },
        { id: 'pharma-cart-summary', type: 'cart-summary', visible: true, props: {} },
      ],
      checkout: [{ id: 'pharma-checkout', type: 'checkout-form', visible: true, props: {} }],
      faq: [
        {
          id: 'pharma-faq',
          type: 'faq-accordion',
          visible: true,
          props: {
            title: 'Conseils & Questions',
            items: [
              { question: 'Les produits sont-ils certifiés ?', answer: 'Oui, tous nos produits sont certifiés par les autorités sanitaires compétentes. Nous ne travaillons qu\'avec des marques reconnues.' },
              { question: 'Puis-je avoir un conseil avant d\'acheter ?', answer: 'Absolument ! Contactez-nous par WhatsApp et notre équipe répondra à vos questions.' },
              { question: 'Les compléments alimentaires sont-ils adaptés à tout le monde ?', answer: 'Certains produits peuvent avoir des contre-indications. Consultez toujours votre médecin en cas de doute.' },
              { question: 'Quels sont les délais de livraison ?', answer: 'Livraison en 24-48h dans toute la Tunisie.' },
            ],
          },
        },
      ],
      about: [
        {
          id: 'pharma-about',
          type: 'rich-text',
          visible: true,
          props: { title: 'Notre Engagement', text: "Notre mission est de rendre les produits de santé et de bien-être accessibles à tous les Tunisiens. Chaque produit est sélectionné avec rigueur par notre équipe de professionnels." },
        },
        { id: 'pharma-about-trust', type: 'trust-strip', visible: true, props: {} },
      ],
      contact: [
        {
          id: 'pharma-contact',
          type: 'rich-text',
          visible: true,
          props: { title: 'Contactez Notre Équipe', text: "WhatsApp : +216 XX XXX XXX\nEmail : contact@votreboutique.tn\nDisponible 7j/7 de 8h à 22h pour vos questions santé." },
        },
      ],
    },
  },
}

// ─── PERFUME / PARFUMERIE ─────────────────────────────────────────────────────

const perfumeTemplate: StoreTemplate = {
  id: 'perfume',
  name: 'Parfumerie & Senteurs',
  description: 'Template haut de gamme pour parfumerie, oud, bakhour, bougies parfumées et cosmétiques de luxe.',
  niche: 'Parfums',
  thumbnail: '/templates/local-webp/bty-cat-parfums.webp',
  layout: {
    theme: {
      id: 'luxury',
      primary: 'hsl(38 80% 30%)',
      secondary: 'hsl(38 50% 96%)',
      backgroundColor: '#fefcf7',
      font: 'Playfair Display',
      radius: '0.25rem',
      ui: {
        productCard: 'luxury',
        buttons: 'pill',
        spacing: 'airy',
        headingStyle: 'editorial',
        font: 'serif',
        density: 'comfortable',
        mode: 'light',
        radius: 'none',
      },
    } as any,
    header: {
      props: {
        storeName: null,
        menuItems: [
          { label: 'Accueil', link: '/' },
          { label: 'Collections', link: '/shop' },
          { label: 'Oud & Bakhour', link: '/category/oud' },
          { label: 'Notre Histoire', link: '/about' },
          { label: 'Contact', link: '/contact' },
        ],
        showSearch: true,
        showCart: true,
        sticky: true,
        variant: 'default',
      },
    },
    footer: {
      props: {
        storeName: true,
        description: 'L\'art des senteurs orientales et occidentales, pour des moments d\'exception.',
        copyright: `© ${new Date().getFullYear()} Votre Boutique. Tous droits réservés.`,
        columns: [
          {
            title: 'Collections',
            links: [
              { label: 'Parfums Homme', link: '/category/homme' },
              { label: 'Parfums Femme', link: '/category/femme' },
              { label: 'Oud & Oriental', link: '/category/oud' },
              { label: 'Bougies Parfumées', link: '/category/bougies' },
            ],
          },
          {
            title: 'Service',
            links: [
              { label: 'Guide des Senteurs', link: '/faq' },
              { label: 'Livraison', link: '/policy/shipping' },
              { label: 'Retours', link: '/policy/returns' },
              { label: 'Contact', link: '/contact' },
            ],
          },
        ],
        socialLinks: { instagram: 'https://instagram.com', facebook: 'https://facebook.com', tiktok: 'https://tiktok.com' },
        showPaymentIcons: true,
      },
    },
    pages: {
      home: [
        // ── 1. Announce bar (luxury tone — no generic "free shipping") ────────
        {
          id: 'perfume-announce',
          type: 'announcement-bar',
          visible: true,
          props: { text: '✨ Livraison gratuite dès 150 TND · Emballage cadeau luxe · Retours 7 jours', backgroundColor: '#2d1a00' },
        },
        // ── 2–3. Editorial chapters before the cinematic hero (magazine pacing)
        {
          id: 'perfume-story1',
          type: 'image-with-text',
          visible: true,
          props: {
            image: '/templates/premium-cosmetics-v1/extrait-ombre-de-figuier-custom.webp',
            imageAlt: "Flacon d'oud oriental, artisanat ancestral",
            title: "L'Héritage Oriental",
            text: "Depuis des siècles, l'oud et les senteurs orientales font partie de notre culture. Nous sélectionnons les meilleurs ouds d'Arabie, d'Inde et d'Indonésie pour vous offrir une expérience olfactive authentique.",
            ctaLabel: 'Collection Oud',
            ctaLink: '/category/oud',
            layout: 'image-right',
            imageAspect: 'portrait',
          },
        },
        {
          id: 'perfume-story2',
          type: 'image-with-text',
          visible: true,
          props: {
            image: '/templates/premium-cosmetics-v1/photophores-verre-depoli-custom.webp',
            imageAlt: 'Emballage prestige, ruban doré',
            title: 'Emballage Prestige',
            text: "Chaque commande est préparée avec le plus grand soin dans un emballage luxueux digne d'un cadeau de prestige. Parfait pour offrir ou se faire plaisir.",
            ctaLabel: 'Commander',
            ctaLink: '/shop',
            layout: 'image-left',
            imageAspect: 'square',
          },
        },
        // ── 4. Cinematic full-height banner — emotional peak after narrative setup
        {
          id: 'perfume-banner',
          type: 'image-banner',
          visible: true,
          props: {
            image: '/templates/premium-cosmetics-v1/eau-de-parfum-rose-orientale-custom.webp',
            title: "L'Art des Senteurs",
            subtitle: 'Des fragrances qui racontent une histoire. Chaque flacon est une invitation au voyage.',
            ctaLabel: 'Explorer les collections',
            ctaLink: '/shop',
            overlayOpacity: 40,
            minHeight: 'xl',
            textAlign: 'center',
          },
        },
        // ── 5. Signature collection — 3 columns = premium spacing ─────────────
        {
          id: 'perfume-featured',
          type: 'featured-collection',
          visible: true,
          props: {
            title: 'Collections Signature',
            subtitle: 'Des fragrances d\'exception, soigneusement sélectionnées',
            limit: 6,
            columns: 3,
            viewAllLabel: 'Explorer tout',
            viewAllLink: '/shop',
          },
        },
        // ── 6. Category grid (browse by gender / scent family / format) ───────
        {
          id: 'perfume-cats',
          type: 'category-grid',
          visible: true,
          props: {},
        },
        // ── 7. Full collection — 3 columns = luxury spacing ───────────────────
        // NO countdown timer — luxury brands never create false urgency
        {
          id: 'perfume-grid',
          type: 'product-grid',
          visible: true,
          props: { title: 'Toute la Collection', subtitle: 'Parfums, oud, bougies et art des senteurs', limit: 12, columns: 3, anchorId: 'collection' },
        },
        // ── 8. Testimonials ──────────────────────────────────────────────────
        {
          id: 'perfume-testimonials',
          type: 'testimonials',
          visible: true,
          props: {
            title: 'Ce que disent nos clients',
            subtitle: 'Avis vérifiés après commande',
            items: [
              { name: 'Mohamed A.', role: 'Client fidèle', text: "L'oud Marocain est exceptionnel — une tenue de 12 heures et des compliments toute la journée. Je ne peux plus m'en passer.", rating: 5 },
              { name: 'Yasmine T.', role: 'Acheteuse vérifiée', text: "Le parfum sent exactement comme décrit. L'emballage était magnifique, parfait pour offrir.", rating: 5 },
              { name: 'Fares K.', role: 'Nouveau client', text: "Première commande et je suis conquis. Livraison rapide, produit authentique. Je recommande.", rating: 5 },
            ],
          },
        },
        // ── 9. Newsletter (exclusive community, not generic promo) ────────────
        {
          id: 'perfume-newsletter',
          type: 'newsletter',
          visible: true,
          props: { title: "L'Exclusive Collection", subtitle: 'Recevez nos nouvelles fragrances et offres privées en avant-première.', placeholder: 'Votre email...', buttonText: 'Rejoindre' },
        },
      ],
      shop: [
        { id: 'perfume-shop-cats', type: 'category-strip', visible: true, props: {} },
        { id: 'perfume-shop-grid', type: 'product-grid', visible: true, props: { title: 'Toutes les Fragrances', limit: 24, columns: 4 } },
      ],
      product: [
        { id: 'perfume-product', type: 'product-details', visible: true, props: {} },
        { id: 'perfume-product-tabs', type: 'product-tabs', visible: true, props: {} },
        { id: 'perfume-related', type: 'related-products', visible: true, props: { title: 'Vous aimerez aussi', limit: 4 } },
      ],
      category: [
        {
          id: 'perfume-cat-announce',
          type: 'announcement-bar',
          visible: true,
          props: {
            text: 'Emballage cadeau offert · Échantillons sur demande · Livraison soignée',
            backgroundColor: '#2d1a00',
          },
        },
        {
          id: 'perfume-cat-editorial',
          type: 'image-with-text',
          visible: true,
          props: {
            image: '/templates/premium-cosmetics-v1/editorial-collection-custom-800.webp',
            imageAlt: 'Flacons de parfum',
            title: 'Une famille olfactive',
            text: 'Chaque catégorie met en avant des accords différents — boisés, floraux, orientaux — pour guider le choix comme en boutique physique.',
            ctaLabel: 'Guide des senteurs',
            ctaLink: '/faq',
            layout: 'image-right',
            imageAspect: 'portrait',
          },
        },
        { id: 'perfume-cat-strip', type: 'category-strip', visible: true, props: {} },
        {
          id: 'perfume-cat-featured',
          type: 'featured-collection',
          visible: true,
          props: {
            title: 'Coup de cœur de la maison',
            subtitle: 'Sélection courte pour prolonger la découverte',
            limit: 4,
            columns: 4,
            viewAllLabel: '',
            viewAllLink: '',
          },
        },
        { id: 'perfume-cat-grid', type: 'category-grid', visible: true, props: {} },
      ],
      cart: [
        { id: 'perfume-cart-lines', type: 'cart-lines', visible: true, props: {} },
        { id: 'perfume-cart-summary', type: 'cart-summary', visible: true, props: {} },
      ],
      checkout: [{ id: 'perfume-checkout', type: 'checkout-form', visible: true, props: {} }],
      faq: [
        {
          id: 'perfume-faq',
          type: 'faq-accordion',
          visible: true,
          props: {
            title: 'Guide des Senteurs',
            items: [
              { question: 'Quelle est la différence entre EDP et EDT ?', answer: 'L\'EDP (Eau de Parfum) est plus concentré (15-20%) et tient 6-8h. L\'EDT (Eau de Toilette) est plus léger (5-15%) et convient pour la journée.' },
              { question: 'L\'oud est-il pour homme ou femme ?', answer: 'L\'oud est universel ! Il existe des ouds masculins, féminins et mixtes. Nos fiches produit précisent l\'orientation olfactive.' },
              { question: 'Comment conserver mon parfum ?', answer: 'Conservez votre parfum à l\'abri de la lumière et des variations de température. Un endroit sombre et frais est idéal.' },
              { question: 'Proposez-vous un emballage cadeau ?', answer: 'Oui, tous nos parfums peuvent être emballés en cadeau luxe. Précisez-le dans les commentaires.' },
            ],
          },
        },
      ],
      about: [
        {
          id: 'perfume-about',
          type: 'rich-text',
          visible: true,
          props: { title: 'Notre Maison', text: "Née de la passion des senteurs orientales et occidentales, notre maison de parfum sélectionne rigoureusement chaque fragrance pour son authenticité, sa qualité et son caractère unique. Chaque flacon raconte une histoire." },
        },
        { id: 'perfume-about-trust', type: 'trust-strip', visible: true, props: {} },
      ],
      contact: [
        {
          id: 'perfume-contact',
          type: 'rich-text',
          visible: true,
          props: { title: 'Contactez-nous', text: "WhatsApp : +216 XX XXX XXX\nEmail : contact@votreboutique.tn\nDisponible 7j/7 de 10h à 22h." },
        },
      ],
    },
  },
}

// ─── PREMIUM TECH (Futurer-style blocks) ─────────────────────────────────────
// Registered in getStoreTemplate so /preview-template/premium-tech/... works.

/** Deep copy so premium-tech pages never share object identity with `electronics` template pages. */
const premiumTechPages = JSON.parse(JSON.stringify(electronicsTemplate.layout.pages)) as StoreTemplate['layout']['pages']

const premiumTechTemplate: StoreTemplate = {
  id: 'premium-tech',
  name: 'Premium Tech (nouveaux blocs)',
  description:
    'Accueil premium : carousel, marquee, product spotlight, catégories éditoriales, scroll showcase, bundle, témoignages dispersés. Configurez productId / productIds dans l’éditeur.',
  niche: 'Électronique',
  thumbnail: '/templates/local-webp/elx-thumbnail.webp',
  layout: {
    ...electronicsTemplate.layout,
    theme: {
      ...(electronicsTemplate.layout.theme as Record<string, unknown>),
      primary: 'hsl(199 89% 48%)',
      backgroundColor: '#f0f9ff',
    } as any,
    // Do not inherit electronics menu/footer category slugs (telephones, tablettes…) — they are absent from `demoCategoriesPremiumTech`.
    header: {
      ...electronicsTemplate.layout.header,
      props: {
        ...electronicsTemplate.layout.header?.props,
        menuItems: [
          { label: 'Accueil', link: '/' },
          { label: 'Boutique', link: '/shop' },
          { label: 'Coques & accessoires', link: '/category/phone-cases' },
          { label: 'Laptops', link: '/category/laptops-tablets' },
          { label: 'Audio', link: '/category/audio' },
          { label: 'Smart home', link: '/category/smart-home' },
          { label: 'FAQ', link: '/faq' },
          { label: 'Contact', link: '/contact' },
        ],
      },
    },
    footer: {
      ...electronicsTemplate.layout.footer,
      props: {
        ...electronicsTemplate.layout.footer?.props,
        description:
          'Coques, audio, laptops & smart home — liens alignés sur le catalogue démo Premium Tech (aperçu).',
        columns: [
          {
            title: 'Produits',
            links: [
              { label: 'Tous les produits', link: '/shop' },
              { label: 'Coques & accessoires', link: '/category/phone-cases' },
              { label: 'Laptops', link: '/category/laptops-tablets' },
              { label: 'Audio', link: '/category/audio' },
              { label: 'Smart home', link: '/category/smart-home' },
            ],
          },
          ...(electronicsTemplate.layout.footer?.props?.columns?.slice(1) ?? []),
        ],
      },
    },
    pages: {
      ...premiumTechPages,
      home: futurerPremiumHomeBlocks.map((b) => ({ ...b, visible: true })),
      shop: futurerPremiumShopBlocks.map((b) => ({ ...b, visible: true })),
      category: futurerPremiumCategoryBlocks.map((b) => ({ ...b, visible: true })),
      product: futurerPremiumProductBlocks.map((b) => ({ ...b, visible: true })),
    },
  },
}

// ─── EXPORTS ──────────────────────────────────────────────────────────────────

export const storeTemplates: StoreTemplate[] = [
  beautyTemplate,
  fashionTemplate,
  fashionEditorialTemplate as StoreTemplate,
  electronicsTemplate,
  generalTemplate,
  foodTemplate,
  kidsTemplate,
  homeDecorTemplate,
  pharmacyTemplate,
  perfumeTemplate,
  premiumTechTemplate,
  ...proStoreTemplates,
]

export const storeTemplatesMap: Record<string, StoreTemplate> = Object.fromEntries(
  storeTemplates.map((t) => [t.id, t])
)

/** Get a template by id */
export function getStoreTemplate(id: string): StoreTemplate | null {
  return storeTemplatesMap[id] ?? null
}

// ── DEV-only self-check ───────────────────────────────────────────────────────
// Validates all templates against their block schemas at module load time.
// Zero runtime cost in production (import.meta.env.DEV is tree-shaken).
if (import.meta.env.DEV) {
  validateBlockCatalogContract()
  validateAllTemplates(storeTemplates)
}
